<script setup lang="ts">
import {
  Activity,
  ArrowUpDown,
  Bike,
  Car,
  Check,
  ChevronDown,
  Download,
  Dumbbell,
  EllipsisVertical,
  Footprints,
  Info,
  Loader2,
  Pencil,
  Pin,
  PinOff,
  Plane,
  Route,
  Sailboat,
  Trash2,
  Train,
  FolderCog,
  FolderPlus,
  Globe,
  Search,
  SquarePlus,
  Users,
  Waves,
  X,
} from '@lucide/vue'
import { computed, nextTick, reactive, ref, watch, onMounted, onUnmounted, markRaw } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import CreateCollectionIcon from '@/components/icons/CreateCollectionIcon.vue'
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import BaseEmptyState from '@/components/ui/BaseEmptyState.vue'
import BaseFilterChip from '@/components/ui/BaseFilterChip.vue'
import BaseMenu from '@/components/ui/BaseMenu.vue'
import BaseMenuItem from '@/components/ui/BaseMenuItem.vue'
import BaseRemovableTag from '@/components/ui/BaseRemovableTag.vue'
import BaseSearchBar from '@/components/ui/BaseSearchBar.vue'
import MobileActionSheet from '@/components/ui/MobileActionSheet.vue'
import { useEventListener } from '@/composables/useEventListener'
import { useSwipeAction } from '@/composables/useSwipeAction'
import { useSyncToMap } from '@/composables/useSyncToMap'
import {
  openAddToCollection as openAddToCollectionPicker,
  tourToAddToCollectionItem,
} from '@/services/addToCollectionService'
import { apiService } from '@/services/apiService'
import CONFIG from '@/services/configService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import { formatByteSize } from '@/services/formattingService'
import { generateId } from '@/services/idService'
import { parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { MOBILE_PANEL_DRAG_START_EVENT } from '@/services/mobilePanelGestureService'
import { openMobileRenameSheet } from '@/services/mobileRenameService'
import { toggleTourPinned } from '@/services/pinnedService'
import { settingsService } from '@/services/settingsService'
import { showError, showSuccess } from '@/services/toastService'
import {
  getTrackTypeCategory,
  getTrackTypeName,
  shouldHideAscentForTourListItem,
} from '@/services/tourTypeService'
import { isSmallScreen } from '@/services/uiService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import { useEventStore } from '@/stores/eventStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useMobileBatchSelectionStore } from '@/stores/mobileBatchSelectionStore'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { EventType } from '@/types/event'
import type { Tour, Track, TourListItem } from '@/types/tour'
import { getTrackFilePickerAccept } from '@/utils/filePickerAccept'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'

import TourDeleteModal from '../modals/TourDeleteModal.vue'

import LibraryTourActionSheetContent from './LibraryTourActionSheetContent.vue'

const emit = defineEmits<{
  'select-workspace': [workspaceId: string, workspaceName: string]
  'navigate-to-workspace': [workspaceId: string, workspaceName: string]
  'open-mobile-tour-detail': [tour: TourListItem]
  'tour-detail-loaded': [tourId: string]
  'zoom-tour': [tourId: string]
}>()

const libraryStore = useLibraryStore()
const mobileBatchSelectionStore = useMobileBatchSelectionStore()
const searchWorkspaceStore = useSearchWorkspaceStore()
const uiStore = useUiStore()
const showLegacyMobileSelectionOverlay = false

// ── In view / map sync ───────────────────────────────────────────────────────

const { lockListToMap, currentMapBounds } = useSyncToMap(
  UI_SETTING_KEYS.libraryAllActivitiesLockListToMap,
)

// ── Type filter ───────────────────────────────────────────────────────────────

type TypeFilterValue = 'all' | 'activity' | 'route'
const typeFilter = ref<TypeFilterValue>('all')
const typeMenuOpen = ref(false)
const typeMenuStyle = ref<Record<string, string>>({})

const typeOptions: { label: string; value: TypeFilterValue; icon?: ReturnType<typeof markRaw> }[] =
  [
    { label: 'All types', value: 'all' },
    { label: 'Activity', value: 'activity', icon: markRaw(Activity) },
    { label: 'Route', value: 'route', icon: markRaw(Route) },
  ]

const activeTypeLabel = computed(() =>
  typeFilter.value === 'all'
    ? 'Type'
    : typeOptions.find((o) => o.value === typeFilter.value)!.label,
)

const activeTypeIcon = computed(
  () => typeOptions.find((o) => o.value === typeFilter.value)?.icon ?? null,
)

const closeAllMenus = () => {
  typeMenuOpen.value = false
  dateMenuOpen.value = false
  collectionMenuOpen.value = false
  distanceMenuOpen.value = false
  sortMenuOpen.value = false
}

// Positions a Teleported dropdown so it never clips the viewport edge.
// dropdownWidthPx must match the dropdown's Tailwind width (w-40=160, w-52=208)
// or a safe estimate for auto-width dropdowns.
const calcMenuStyle = (event: MouseEvent, dropdownWidthPx: number): Record<string, string> => {
  const btn = event.currentTarget as HTMLElement
  const rect = btn.getBoundingClientRect()
  const MARGIN = 8
  const bottomInset = isSmallScreen.value ? (uiStore.libraryPanelHeight ?? 58) : 0
  const top = rect.bottom + 4
  const left = Math.max(MARGIN, Math.min(rect.left, window.innerWidth - dropdownWidthPx - MARGIN))
  const maxHeight = Math.max(120, window.innerHeight - top - MARGIN - bottomInset)
  return {
    top: `${top}px`,
    left: `${left}px`,
    maxHeight: `${maxHeight}px`,
  }
}

const toggleTypeMenu = (event: MouseEvent) => {
  const wasOpen = typeMenuOpen.value
  closeAllMenus()
  typeMenuStyle.value = calcMenuStyle(event, 160) // w-40
  typeMenuOpen.value = !wasOpen
}

const selectTypeOption = (value: TypeFilterValue) => {
  typeFilter.value = value
  typeMenuOpen.value = false
}

const getTourDisplayType = (tour: TourListItem): string => {
  if (tour.type) {
    return getTrackTypeName(tour.type) ?? tour.type
  }
  return tour.startDate ? 'Activity' : 'Route'
}

const getTourTypeIcon = (tour: TourListItem) => {
  if (!tour.type) {
    return tour.startDate ? Activity : Route
  }
  const category = getTrackTypeCategory(tour.type)
  switch (category) {
    case 'aviation':
      return Plane
    case 'cycling':
      return Bike
    case 'driving':
      return Car
    case 'rail':
      return Train
    case 'hiking':
    case 'running':
    case 'walking':
      return Footprints
    case 'swimming':
    case 'diving':
      return Waves
    case 'water_sports':
      return Sailboat
    case 'gym_fitness_equipment':
      return Dumbbell
    default:
      return Activity
  }
}

const getVisibleTourElevationGain = (tour: TourListItem): number | null => {
  if (shouldHideAscentForTourListItem(tour)) return null
  return tour.elevationGain ?? null
}

const isTourActivity = (tour: TourListItem): boolean => !!tour.type || !!tour.startDate

const isTourMatchingSearch = (tour: TourListItem): boolean => {
  const query = libraryStore.searchQuery.trim().toLowerCase()
  if (!query) return true
  if ((tour.name ?? '').toLowerCase().includes(query)) return true
  return tour.workspaces.some((workspace) => workspace.name.toLowerCase().includes(query))
}

const mobileListRef = ref<HTMLElement | null>(null)
const desktopScrollerRef = ref<HTMLElement | null>(null)
const isListScrolling = ref(false)
let listScrollResetTimer: ReturnType<typeof setTimeout> | null = null

// Tracks whether the cursor is currently over a list row.
function onListRowMouseEnter(tourId: string): void {
  if (isListScrolling.value) return
  libraryStore.setHoveredTour(tourId)
}

function onListRowMouseLeave(): void {
  if (isListScrolling.value) return
  libraryStore.clearHoveredTour()
}

function onListScroll(): void {
  if (!isListScrolling.value) {
    isListScrolling.value = true
    libraryStore.clearHoveredTour()
  }

  if (listScrollResetTimer !== null) {
    clearTimeout(listScrollResetTimer)
  }

  listScrollResetTimer = setTimeout(() => {
    isListScrolling.value = false
    listScrollResetTimer = null
  }, 150)
}

onUnmounted(() => {
  if (listScrollResetTimer !== null) {
    clearTimeout(listScrollResetTimer)
  }
})

watch(
  () => libraryStore.scrollTargetTourId,
  (tourId) => {
    if (!tourId) return
    const scroller = isSmallScreen.value ? mobileListRef.value : desktopScrollerRef.value
    if (scroller) {
      const row = scroller.querySelector(
        `[data-tour-id="${CSS.escape(tourId)}"]`,
      ) as HTMLElement | null
      if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
    libraryStore.clearScrollTarget()
  },
)

// ── Columns ─────────────────────────────────────────────────────────────────

interface Column {
  key: string
  label: string
  sortable: boolean
}

const columns: Column[] = [{ key: 'name', label: 'Name', sortable: true }]

// ── Client-side sorting ──────────────────────────────────────────────────────

const savedSort =
  settingsService.getSetting<string>(UI_SETTING_KEYS.libraryAllActivitiesSort) ?? 'date-desc'
const savedSortDashIdx = savedSort.lastIndexOf('-')
const sortCol = ref<string>(savedSort.slice(0, savedSortDashIdx) || 'date')
const sortDir = ref<'asc' | 'desc'>(
  (savedSort.slice(savedSortDashIdx + 1) as 'asc' | 'desc') || 'desc',
)
let applyingRemoteSort = false

settingsService.subscribe(({ key, value, source }) => {
  if (
    source !== 'remote' ||
    key !== UI_SETTING_KEYS.libraryAllActivitiesSort ||
    typeof value !== 'string'
  ) {
    return
  }
  const dashIdx = value.lastIndexOf('-')
  applyingRemoteSort = true
  sortCol.value = value.slice(0, dashIdx) || 'date'
  sortDir.value = (value.slice(dashIdx + 1) as 'asc' | 'desc') || 'desc'
  applyingRemoteSort = false
})

const tourMaxDistanceKm = computed(() => {
  let max = 0
  for (const t of libraryStore.tours) {
    if (t.distance != null && t.distance / 1000 > max) max = t.distance / 1000
  }
  return Math.ceil(max) || 100
})

const distanceFilterMin = ref(0)
const distanceFilterMax = ref(tourMaxDistanceKm.value)

// Keep distanceFilterMax spanning the full range unless the user has changed it
watch(
  tourMaxDistanceKm,
  (newMax, oldMax) => {
    if (distanceFilterMax.value === oldMax) distanceFilterMax.value = newMax
  },
  { immediate: true },
)

// Prevent thumb crossover
watch(distanceFilterMin, (val) => {
  if (val > distanceFilterMax.value) distanceFilterMax.value = val
})
watch(distanceFilterMax, (val) => {
  if (val < distanceFilterMin.value) distanceFilterMin.value = val
})

const distanceTrackStyle = computed(() => ({
  left: `${(distanceFilterMin.value / tourMaxDistanceKm.value) * 100}%`,
  right: `${100 - (distanceFilterMax.value / tourMaxDistanceKm.value) * 100}%`,
}))

// ── Date preset chips ────────────────────────────────────────────────────────

type DatePreset = 'any' | 'last_week' | 'last_month' | 'last_6m' | 'last_year' | 'custom'
const datePreset = ref<DatePreset>('any')
const dateMenuOpen = ref(false)
const dateMenuStyle = ref<Record<string, string>>({})

const datePresetOptions: { label: string; value: DatePreset }[] = [
  { label: 'Any time', value: 'any' },
  { label: 'Last week', value: 'last_week' },
  { label: 'Last month', value: 'last_month' },
  { label: 'Last 6 months', value: 'last_6m' },
  { label: 'Last year', value: 'last_year' },
  { label: 'Custom range', value: 'custom' },
]

const toISODate = (d: Date) => d.toISOString().slice(0, 10)

const dateCustomSectionRef = ref<HTMLElement | null>(null)

const selectDateOption = (preset: DatePreset) => {
  if (preset !== 'custom') dateMenuOpen.value = false
  datePreset.value = preset
  if (preset === 'custom') {
    nextTick(() =>
      dateCustomSectionRef.value?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
    )
  }
  const now = new Date()
  if (preset === 'any') {
    libraryStore.setDateRange(null, null)
  } else if (preset === 'last_week') {
    const from = new Date(now)
    from.setDate(now.getDate() - 7)
    libraryStore.setDateRange(toISODate(from), null)
  } else if (preset === 'last_month') {
    const from = new Date(now)
    from.setMonth(now.getMonth() - 1)
    libraryStore.setDateRange(toISODate(from), null)
  } else if (preset === 'last_6m') {
    const from = new Date(now)
    from.setMonth(now.getMonth() - 6)
    libraryStore.setDateRange(toISODate(from), null)
  } else if (preset === 'last_year') {
    const from = new Date(now)
    from.setFullYear(now.getFullYear() - 1)
    libraryStore.setDateRange(toISODate(from), null)
  }
  if (preset === 'custom') {
    // Pre-fill "To" with today if not already set
    if (!libraryStore.dateTo) {
      libraryStore.setDateRange(libraryStore.dateFrom, toISODate(now))
    }
  }
}

// ── Collection filter ────────────────────────────────────────────────────────

type CollectionFilterValue = 'all' | 'unassigned' | string // string = workspace clientId
const collectionFilter = ref<CollectionFilterValue>('all')
const collectionMenuOpen = ref(false)
const collectionMenuStyle = ref<Record<string, string>>({})

const activeCollectionLabel = computed(() => {
  if (collectionFilter.value === 'all') return 'Collection'
  if (collectionFilter.value === 'unassigned') return 'Unassigned'
  const ws = searchWorkspaceStore.allWorkspaces.find((w) => w.clientId === collectionFilter.value)
  return ws ? truncateCollection(ws.name) : 'Collection'
})

const collectionSearchQuery = ref('')

const filteredCollectionOptions = computed(() => {
  const q = collectionSearchQuery.value.toLowerCase().trim()
  const owned = searchWorkspaceStore.allWorkspaces.filter((w) => w.isOwner)
  return q ? owned.filter((w) => w.name.toLowerCase().includes(q)) : owned
})

const toggleCollectionMenu = (event: MouseEvent) => {
  const wasOpen = collectionMenuOpen.value
  closeAllMenus()
  collectionMenuStyle.value = calcMenuStyle(event, 208) // w-52
  collectionMenuOpen.value = !wasOpen
  collectionSearchQuery.value = ''
}

const selectCollectionOption = (value: CollectionFilterValue) => {
  collectionFilter.value = value
  collectionMenuOpen.value = false
  collectionSearchQuery.value = ''
}

// ── Distance preset dropdown ─────────────────────────────────────────────────

type DistancePreset = 'all' | '0-10' | '0-100' | 'custom'
const distancePreset = ref<DistancePreset>('all')
const distanceMenuOpen = ref(false)
const distanceMenuStyle = ref<Record<string, string>>({})

const distanceOptions: { label: string; value: DistancePreset }[] = [
  { label: 'All distances', value: 'all' },
  { label: '0–10 km', value: '0-10' },
  { label: '0–100 km', value: '0-100' },
  { label: 'Custom range', value: 'custom' },
]

const formatDateLabel = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00')
  const currentYear = new Date().getFullYear()
  return d.getFullYear() === currentYear
    ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
}

const activeDateLabel = computed(() => {
  if (datePreset.value === 'any') return 'Date'
  if (datePreset.value === 'custom') {
    const from = libraryStore.dateFrom
    const to = libraryStore.dateTo
    if (from && to) return `${formatDateLabel(from)} – ${formatDateLabel(to)}`
    if (from) return `From ${formatDateLabel(from)}`
    if (to) return `To ${formatDateLabel(to)}`
    return 'Custom range'
  }
  return datePresetOptions.find((o) => o.value === datePreset.value)?.label ?? 'Date'
})

const activeDistanceLabel = computed(() => {
  if (distancePreset.value === 'all') return 'Distance'
  if (distancePreset.value === 'custom')
    return `${distanceFilterMin.value}–${distanceFilterMax.value} km`
  return distanceOptions.find((o) => o.value === distancePreset.value)?.label ?? 'Distance'
})

const toggleDateMenu = (event: MouseEvent) => {
  const wasOpen = dateMenuOpen.value
  closeAllMenus()
  dateMenuStyle.value = calcMenuStyle(event, 200) // whitespace-nowrap, ~200px
  dateMenuOpen.value = !wasOpen
}

const toggleDistanceMenu = (event: MouseEvent) => {
  const wasOpen = distanceMenuOpen.value
  closeAllMenus()
  distanceMenuStyle.value = calcMenuStyle(event, 220) // whitespace-nowrap + w-44 slider
  distanceMenuOpen.value = !wasOpen
}

const selectDistanceOption = (preset: DistancePreset) => {
  if (preset !== 'custom') distanceMenuOpen.value = false
  distancePreset.value = preset
  if (preset === 'all') {
    distanceFilterMin.value = 0
    distanceFilterMax.value = tourMaxDistanceKm.value
  } else if (preset === '0-10') {
    distanceFilterMin.value = 0
    distanceFilterMax.value = 10
  } else if (preset === '0-100') {
    distanceFilterMin.value = 0
    distanceFilterMax.value = 100
  }
  // 'custom': keep values, inline slider will appear
}

const truncateCollection = (name: string) => (name.length > 20 ? name.slice(0, 20) + '…' : name)

// ── Sort chip ────────────────────────────────────────────────────────────────

type SortKey =
  | 'name-asc'
  | 'name-desc'
  | 'date-desc'
  | 'date-asc'
  | 'updatedAt-desc'
  | 'updatedAt-asc'
  | 'distance-asc'
  | 'distance-desc'
  | 'elevation-asc'
  | 'elevation-desc'

const DEFAULT_SORT_KEY: SortKey = 'date-desc'

const sortMenuOpen = ref(false)
const sortMenuStyle = ref<Record<string, string>>({})

const sortOptions: { label: string; chipLabel: string; value: SortKey }[] = [
  { label: 'Name (A to Z)', chipLabel: 'Name A–Z', value: 'name-asc' },
  { label: 'Name (Z to A)', chipLabel: 'Name Z–A', value: 'name-desc' },
  { label: 'Newest first (default)', chipLabel: 'Newest first', value: 'date-desc' },
  { label: 'Oldest first', chipLabel: 'Oldest first', value: 'date-asc' },
  { label: 'Recently modified', chipLabel: 'Recently modified', value: 'updatedAt-desc' },
  { label: 'Least recently modified', chipLabel: 'Least modified', value: 'updatedAt-asc' },
  { label: 'Distance (low to high)', chipLabel: 'Distance ↑', value: 'distance-asc' },
  { label: 'Distance (high to low)', chipLabel: 'Distance ↓', value: 'distance-desc' },
  { label: 'Elevation (low to high)', chipLabel: 'Elevation ↑', value: 'elevation-asc' },
  { label: 'Elevation (high to low)', chipLabel: 'Elevation ↓', value: 'elevation-desc' },
]

const activeSortKey = computed<SortKey>(() => `${sortCol.value}-${sortDir.value}` as SortKey)

const isSortActive = computed(() => activeSortKey.value !== DEFAULT_SORT_KEY)

const activeSortChipLabel = computed(() => {
  if (!isSortActive.value) return 'Sort'
  return sortOptions.find((o) => o.value === activeSortKey.value)?.chipLabel ?? 'Sort'
})

const toggleSortMenu = (event: MouseEvent) => {
  const wasOpen = sortMenuOpen.value
  closeAllMenus()
  sortMenuStyle.value = calcMenuStyle(event, 208) // w-52
  sortMenuOpen.value = !wasOpen
}

const selectSortOption = (value: SortKey) => {
  const dashIdx = value.lastIndexOf('-')
  sortCol.value = value.slice(0, dashIdx)
  sortDir.value = value.slice(dashIdx + 1) as 'asc' | 'desc'
  sortMenuOpen.value = false
}

watch(
  [sortCol, sortDir],
  () => {
    if (applyingRemoteSort) return
    settingsService.saveSetting(
      UI_SETTING_KEYS.libraryAllActivitiesSort,
      `${sortCol.value}-${sortDir.value}`,
    )
  },
  { flush: 'sync' },
)

const hasActiveFilters = computed(
  () =>
    !!(
      libraryStore.searchQuery ||
      typeFilter.value !== 'all' ||
      collectionFilter.value !== 'all' ||
      libraryStore.dateFrom ||
      libraryStore.dateTo ||
      libraryStore.showOnlyUploadedBatch ||
      distanceFilterMin.value > 0 ||
      distanceFilterMax.value < tourMaxDistanceKm.value
    ),
)

const resetLocalFilters = () => {
  typeFilter.value = 'all'
  collectionFilter.value = 'all'
  datePreset.value = 'any'
  libraryStore.setDateRange(null, null)
  distancePreset.value = 'all'
  distanceFilterMin.value = 0
  distanceFilterMax.value = tourMaxDistanceKm.value
  if (libraryStore.showOnlyUploadedBatch) libraryStore.clearUploadedBatchFilter()
}

// Clear pinned tours (newly drawn routes) whenever the user changes a
// component-local filter — they should then be subject to normal filtering.
watch([collectionFilter, typeFilter, distanceFilterMin, distanceFilterMax, lockListToMap], () => {
  libraryStore.clearPinnedTours()
})

const tourCountLabel = computed(() => {
  const total = libraryStore.tours.length
  const filtered = sortedTours.value.length
  return filtered !== total ? `${filtered}/${total}` : `${filtered}`
})

const sortedTours = computed(() => {
  const recentIds = libraryStore.recentlyUploadedIds
  const batchIds = libraryStore.uploadedBatchIds
  const showOnlyBatch = libraryStore.showOnlyUploadedBatch

  const list = showOnlyBatch
    ? libraryStore.tours.filter((t) => batchIds.has(t.clientId))
    : [...libraryStore.tours]

  list.sort((a, b) => {
    const aRecent = recentIds.has(a.clientId)
    const bRecent = recentIds.has(b.clientId)
    if (aRecent !== bRecent) return aRecent ? -1 : 1

    let av: string | number | null | undefined
    let bv: string | number | null | undefined
    if (sortCol.value === 'name') {
      av = a.name?.toLowerCase() ?? ''
      bv = b.name?.toLowerCase() ?? ''
    } else if (sortCol.value === 'workspace') {
      av = a.workspaces
        .map((w) => w.name)
        .join(', ')
        .toLowerCase()
      bv = b.workspaces
        .map((w) => w.name)
        .join(', ')
        .toLowerCase()
    } else if (sortCol.value === 'distance') {
      av = a.distance ?? -1
      bv = b.distance ?? -1
    } else if (sortCol.value === 'elevation') {
      av = a.elevationGain ?? -1
      bv = b.elevationGain ?? -1
    } else if (sortCol.value === 'type') {
      av = getTourDisplayType(a)
      bv = getTourDisplayType(b)
    } else if (sortCol.value === 'updatedAt') {
      av = a.updatedAt ?? a.createdAt ?? ''
      bv = b.updatedAt ?? b.createdAt ?? ''
    } else {
      av = a.startDate ?? a.createdAt ?? ''
      bv = b.startDate ?? b.createdAt ?? ''
    }
    if (av === bv) return 0
    const cmp = av < bv ? -1 : 1
    return sortDir.value === 'asc' ? cmp : -cmp
  })

  // Type filter
  let filtered = list as TourListItem[]
  filtered = filtered.filter(isTourMatchingSearch)

  if (typeFilter.value === 'activity') {
    filtered = filtered.filter(isTourActivity)
  } else if (typeFilter.value === 'route') {
    filtered = filtered.filter((t) => !isTourActivity(t))
  }

  // Collection filter
  if (collectionFilter.value === 'unassigned') {
    filtered = filtered.filter((t) => t.workspaces.length === 0)
  } else if (collectionFilter.value !== 'all') {
    filtered = filtered.filter((t) =>
      t.workspaces.some((w) => w.clientId === collectionFilter.value),
    )
  }

  // Date filter
  filtered = filtered.filter(isTourWithinDateRange)

  // In view filter
  if (lockListToMap.value && currentMapBounds.value !== null) {
    const vp = currentMapBounds.value
    filtered = filtered.filter((tour) => {
      for (const track of tour.tracks) {
        if (!track.pointsLineReduced500m) continue
        const coords = parseLineString(track.pointsLineReduced500m)
        for (const [lng, lat] of coords) {
          if (lng >= vp.west && lng <= vp.east && lat >= vp.south && lat <= vp.north) {
            return true
          }
        }
      }
      return false
    })
  }

  // Distance filter
  const minM = distanceFilterMin.value * 1000
  const maxM = distanceFilterMax.value * 1000
  if (distanceFilterMin.value > 0 || distanceFilterMax.value < tourMaxDistanceKm.value) {
    filtered = filtered.filter((t) => {
      const d = t.distance
      if (d == null) return false
      return d >= minM && d <= maxM
    })
  }

  // Always show pinned tours (newly drawn routes) regardless of active filters,
  // until the user explicitly changes a filter (which clears pinnedTourIds).
  const pinnedIds = libraryStore.pinnedTourIds
  if (pinnedIds.size > 0) {
    const presentIds = new Set(filtered.map((t) => t.clientId))
    const missing = libraryStore.tours.filter(
      (t) => pinnedIds.has(t.clientId) && !presentIds.has(t.clientId),
    )
    if (missing.length > 0) filtered = [...missing, ...filtered]
  }

  // Always show selected tours regardless of active filters.
  const selectedIds = libraryStore.selectedTourIds
  if (selectedIds.size > 0) {
    const presentIds = new Set(filtered.map((t) => t.clientId))
    const missing = libraryStore.tours.filter(
      (t) => selectedIds.has(t.clientId) && !presentIds.has(t.clientId),
    )
    if (missing.length > 0) filtered = [...missing, ...filtered]
  }

  return filtered
})

const formatDistance = (meters: number | null | undefined): string => {
  if (meters == null) return '—'
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`
  return `${Math.round(meters)} m`
}

const formatElevation = (meters: number | null | undefined): string => {
  if (meters == null) return '—'
  return `${Math.round(meters)} m`
}

const formatDistanceSum = (meters: number | null | undefined): string => {
  if (meters == null) return '—'
  if (meters >= 1000) return `${Math.round(meters / 1000).toLocaleString()} km`
  return `${Math.round(meters).toLocaleString()} m`
}

const formatElevationSum = (meters: number | null | undefined): string => {
  if (meters == null) return '—'
  return `${Math.round(meters).toLocaleString()} m`
}

const formatDuration = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string | null => {
  if (!startDate || !endDate) return null
  const ms = Date.parse(endDate) - Date.parse(startDate)
  if (ms <= 0) return null
  const totalMin = Math.round(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  const isCurrentYear = d.getFullYear() === new Date().getFullYear()
  if (isCurrentYear) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  const base = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  const shortYear = `'${String(d.getFullYear()).slice(2)}`
  return base.replace(String(d.getFullYear()), shortYear)
}

const isTourWithinDateRange = (tour: TourListItem): boolean => {
  if (!libraryStore.dateFrom && !libraryStore.dateTo) return true
  if (!tour.startDate) return true
  const start = Date.parse(tour.startDate)
  if (Number.isNaN(start)) return true
  if (libraryStore.dateFrom && start < Date.parse(libraryStore.dateFrom)) return false
  if (libraryStore.dateTo && start > Date.parse(libraryStore.dateTo)) return false
  return true
}

// ── Summary stats ────────────────────────────────────────────────────────────

const selectedDistance = computed(() => {
  if (libraryStore.selectedTourIds.size === 0) return null
  let sum = 0
  let hasValue = false
  for (const t of sortedTours.value) {
    if (libraryStore.selectedTourIds.has(t.clientId) && t.distance != null) {
      sum += t.distance
      hasValue = true
    }
  }
  return hasValue ? sum : null
})

const selectedElevationGain = computed(() => {
  if (libraryStore.selectedTourIds.size === 0) return null
  let sum = 0
  let hasValue = false
  for (const t of sortedTours.value) {
    const elevationGain = getVisibleTourElevationGain(t)
    if (libraryStore.selectedTourIds.has(t.clientId) && elevationGain != null) {
      sum += elevationGain
      hasValue = true
    }
  }
  return hasValue ? sum : null
})

// ── Workspace inline remove ──────────────────────────────────────────────────

const closeMenu = () => {}

const handleDocumentClick = (event: MouseEvent) => {
  closeMenu()
  const target = event.target as Element
  if (!target.closest('[data-type-menu]')) typeMenuOpen.value = false
  if (!target.closest('[data-date-menu]')) dateMenuOpen.value = false
  if (!target.closest('[data-distance-menu]')) distanceMenuOpen.value = false
  if (!target.closest('[data-collection-menu]')) collectionMenuOpen.value = false
  if (!target.closest('[data-sort-menu]')) sortMenuOpen.value = false
}
onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  window.addEventListener('trigger-library-import', handleTriggerLibraryImport)
  pillsResizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const el = entry.target as HTMLElement
      const tourId = el.dataset.pillsWrapper
      if (!tourId) continue
      // Reset to undefined so all pills render in the probe, then re-measure
      delete pillsVisibleCounts[tourId]
      nextTick(() => {
        pillsVisibleCounts[tourId] = computeVisiblePillCount(el)
      })
    }
  })
})
onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
  window.removeEventListener('trigger-library-import', handleTriggerLibraryImport)
  pillsResizeObserver?.disconnect()
  pillsResizeObserver = null
})

// ── Desktop collection pill overflow tracking ────────────────────────────────

const pillsContainerEls: Record<string, HTMLElement> = {}
const pillsVisibleCounts = reactive<Record<string, number>>({})
let pillsResizeObserver: ResizeObserver | null = null

const computeVisiblePillCount = (wrapper: HTMLElement): number => {
  const probe = wrapper.querySelector<HTMLElement>('[data-pills-probe]')
  if (!probe) return 0
  const pills = probe.querySelectorAll<HTMLElement>('[data-pill-item]')
  const probeRight = probe.getBoundingClientRect().right
  let count = 0
  for (const pill of pills) {
    if (pill.getBoundingClientRect().right <= probeRight + 0.5) count++
    else break
  }
  return count
}

const registerPillsContainer = (el: Element | null, tourId: string) => {
  if (el instanceof HTMLElement) {
    pillsContainerEls[tourId] = el
    pillsResizeObserver?.observe(el)
    // Defer until the v-for probe children have rendered
    nextTick(() => {
      pillsVisibleCounts[tourId] = computeVisiblePillCount(el)
    })
  } else {
    const prev = pillsContainerEls[tourId]
    if (prev) pillsResizeObserver?.unobserve(prev)
    delete pillsContainerEls[tourId]
    delete pillsVisibleCounts[tourId]
  }
}

// isSelectionMode lives in libraryStore so LibraryTourLayer can also read/set it.

// Reload tours and reset selection only when actually entering this view.
// The component stays mounted (v-show) so onMounted fires at app startup;
// using a watcher on isAllActivitiesView avoids loading data — and adding
// tracks to the map — before the user has navigated here.
watch(
  () => uiStore.isAllActivitiesView,
  async (isActive) => {
    if (!isActive) {
      // Leaving the view - clear so the map doesn't stay filtered.
      libraryStore.setClientFilter(null)
      libraryStore.exitSelectionMode()
      libraryStore.clearSelectedTours()
      return
    }
    // Reset non-persistent filters on every entry (sort + in-view are persisted separately)
    resetLocalFilters()
    libraryStore.setSelectedTourIds([])
    await searchWorkspaceStore.ensureWorkspacesLoaded()
    await libraryStore.loadTours()
  },
  { immediate: true },
)

// Keep the map in sync with client-side filters.
watch(
  [
    () => libraryStore.searchQuery,
    typeFilter,
    collectionFilter,
    () => libraryStore.dateFrom,
    () => libraryStore.dateTo,
    distanceFilterMin,
    distanceFilterMax,
    tourMaxDistanceKm,
    () => libraryStore.tours,
  ],
  () => {
    const hasFilter =
      !!libraryStore.searchQuery.trim() ||
      typeFilter.value !== 'all' ||
      collectionFilter.value !== 'all' ||
      !!libraryStore.dateFrom ||
      !!libraryStore.dateTo ||
      distanceFilterMin.value > 0 ||
      distanceFilterMax.value < tourMaxDistanceKm.value

    if (!hasFilter) {
      libraryStore.setClientFilter(null)
      return
    }

    let filtered = libraryStore.tours as TourListItem[]
    filtered = filtered.filter(isTourMatchingSearch)

    if (typeFilter.value === 'activity') {
      filtered = filtered.filter(isTourActivity)
    } else if (typeFilter.value === 'route') {
      filtered = filtered.filter((tour) => !isTourActivity(tour))
    }

    if (collectionFilter.value === 'unassigned') {
      filtered = filtered.filter((tour) => tour.workspaces.length === 0)
    } else if (collectionFilter.value !== 'all') {
      filtered = filtered.filter((tour) =>
        tour.workspaces.some((workspace) => workspace.clientId === collectionFilter.value),
      )
    }

    filtered = filtered.filter(isTourWithinDateRange)

    if (distanceFilterMin.value > 0 || distanceFilterMax.value < tourMaxDistanceKm.value) {
      const minM = distanceFilterMin.value * 1000
      const maxM = distanceFilterMax.value * 1000
      filtered = filtered.filter(
        (tour) => tour.distance != null && tour.distance >= minM && tour.distance <= maxM,
      )
    }

    libraryStore.setClientFilter(new Set(filtered.map((tour) => tour.clientId)))
  },
  { immediate: true },
)

const workspaces = computed(() => searchWorkspaceStore.allWorkspaces)

const removeFromWorkspace = async (tour: TourListItem, workspaceClientId: string) => {
  try {
    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    workspaceStore._createEventPushed = true

    await eventStore.addEvent({
      type: EventType.TOUR_REMOVED_FROM_WORKSPACE,
      payload: {
        client_id: tour.clientId,
        workspace_client_id: workspaceClientId,
      },
    })

    tour.workspaces = tour.workspaces.filter((w) => w.clientId !== workspaceClientId)
    logService.info(LOG_CATEGORIES.UI, 'Removed tour from workspace', {
      tourId: tour.clientId,
      workspaceClientId,
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to remove tour from workspace', { error })
    showError(
      `Failed to remove from workspace: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

// ── Upload ────────────────────────────────────────────────────────────────────

const fileInputRef = ref<HTMLInputElement | null>(null)
const trackFilePickerAccept = getTrackFilePickerAccept()
const isUploading = ref(false)

const handleTriggerLibraryImport = () => {
  fileInputRef.value?.click()
}

const uploadFiles = async (files: File[]) => {
  const validFiles = files.filter((f) => {
    const name = f.name.toLowerCase()
    if (!name.endsWith('.gpx') && !name.endsWith('.fit')) {
      showError(`Unsupported file type: ${f.name}`)
      return false
    }
    if (f.size > CONFIG.UPLOAD_MAX_FILE_SIZE) {
      showError(
        `File "${f.name}" exceeds the maximum size of ${formatByteSize(CONFIG.UPLOAD_MAX_FILE_SIZE)}.`,
      )
      return false
    }
    return true
  })
  if (validFiles.length === 0) return

  isUploading.value = true
  try {
    await libraryStore.uploadTourFiles(validFiles)
    showSuccess(`${validFiles.length} file${validFiles.length > 1 ? 's' : ''} added.`)
  } catch (error) {
    logService.error(LOG_CATEGORIES.FILE, 'Upload failed', { error })
    showError(`Upload failed: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    isUploading.value = false
  }
}

const handleFileInput = async (e: Event) => {
  const input = e.target as HTMLInputElement
  if (!input.files) return
  await uploadFiles(Array.from(input.files))
  input.value = ''
}

// ── Selection ─────────────────────────────────────────────────────────────────

// Mobile-only selection mode: checkboxes are hidden until this is true.
// Exposed so MobileToolbar can read it and render the selection header overlay.
const exitSelectionMode = () => {
  mobileBatchSelectionStore.exit()
  libraryStore.exitSelectionMode()
}

// ── Long-press to enter selection mode (mobile) ───────────────────────────────

const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const longPressActivated = ref(false)

const startLongPress = (tourId: string) => {
  longPressTimer.value = setTimeout(() => {
    longPressTimer.value = null
    longPressActivated.value = true
    try {
      navigator.vibrate(30)
    } catch (error) {
      logService.debug(LOG_CATEGORIES.UI, 'Haptic feedback unavailable for library long press', {
        error,
      })
    }
    const tour = sortedTours.value.find((t) => t.clientId === tourId)
    if (tour) {
      emit('zoom-tour', tour.clientId)
      openRowContextMenu(tour)
    }
  }, 500)
}

const cancelLongPress = () => {
  if (longPressTimer.value !== null) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
  }
}

const cancelLibraryContextMenuForPanelDrag = () => {
  cancelLongPress()
  longPressActivated.value = false
  closeContextMenu()
}

useEventListener(window, MOBILE_PANEL_DRAG_START_EVENT, cancelLibraryContextMenuForPanelDrag)

const isAllSelected = computed(() => {
  if (sortedTours.value.length === 0) return false
  return sortedTours.value.every((t) => libraryStore.selectedTourIds.has(t.clientId))
})

const isSomeSelected = computed(() => libraryStore.selectedTourIds.size > 0 && !isAllSelected.value)

// Refs for the master checkbox (desktop count row / desktop selection overlay).
// The indeterminate property can only be set via JS, not an HTML attribute.
const getRequiredSelectedTourIds = () => {
  return uiStore.isTrackEditMode && uiStore.editingTrackTourId ? [uiStore.editingTrackTourId] : []
}

const notifySelectionChanged = () => {
  window.dispatchEvent(new CustomEvent('library-selection-changed'))
}

// Mobile swipe-to-pin gesture. The helper handles horizontal detection, threshold
// haptics, and click suppression after a completed swipe.
const tourSwipe = useSwipeAction<TourListItem>({
  getId: (tour) => tour.clientId,
  isDisabled: () => _filterBarTouching,
  onCancelCompetingGesture: cancelLongPress,
  onTrigger: (tour) => handleTogglePinned(tour),
})

let _filterBarTouching = false

const onFilterBarTouchStart = () => {
  _filterBarTouching = true
}
const onFilterBarTouchEnd = () => {
  _filterBarTouching = false
}

// Wrapper handlers — combine swipe + long-press calls in a single function
// so the template never needs multi-statement inline handlers (which break the formatter).
const onRowTouchStart = (e: TouchEvent, tour: TourListItem) => {
  // Don't start swipe or long-press while the row's name field is being edited
  if (renamingTourId.value === tour.clientId) return
  tourSwipe.start(e, tour)
  startLongPress(tour.clientId)
}
const onRowTouchEnd = (tour: TourListItem) => {
  tourSwipe.end(tour)
  cancelLongPress()
}
const onRowTouchCancel = (tour: TourListItem) => {
  tourSwipe.cancel(tour)
  cancelLongPress()
}

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    // All selected → clear
    libraryStore.setSelectedTourIds(getRequiredSelectedTourIds())
  } else {
    libraryStore.setSelectedTourIds([
      ...sortedTours.value.map((tour) => tour.clientId),
      ...getRequiredSelectedTourIds(),
    ])
  }
  mobileBatchSelectionStore.setSelected('tours', libraryStore.selectedTourIds)
  notifySelectionChanged()
}

const toggleSelect = (tourId: string) => {
  if (
    uiStore.isTrackEditMode &&
    uiStore.editingTrackTourId === tourId &&
    libraryStore.selectedTourIds.has(tourId)
  ) {
    return
  }
  libraryStore.toggleSelectedTour(tourId)
  mobileBatchSelectionStore.setSelected('tours', libraryStore.selectedTourIds)
  notifySelectionChanged()
  window.dispatchEvent(new CustomEvent('library-zoom-to-tour', { detail: { tourId } }))
}

const handleTourClick = (tour: TourListItem, event?: MouseEvent) => {
  if (event?.target instanceof HTMLInputElement && event.target.type === 'checkbox') return
  if (tourSwipe.consumeSuppressedClick()) return
  if (longPressActivated.value) {
    longPressActivated.value = false
    return
  }
  if (libraryStore.isSelectionMode || mobileBatchSelectionStore.activeSurface === 'library') {
    toggleSelect(tour.clientId)
    return
  }
  window.dispatchEvent(
    new CustomEvent('library-zoom-to-tour', { detail: { tourId: tour.clientId } }),
  )
}

const handleInfoClick = (event: MouseEvent, tour: TourListItem) => {
  event.stopPropagation()
  libraryStore.selectTourForDetail(tour, { focusMap: false })
  window.dispatchEvent(new CustomEvent('expand-tour-profile'))
}

watch(
  () => [uiStore.isTrackEditMode, uiStore.editingTrackTourId] as const,
  ([isTrackEditMode, editingTrackTourId]) => {
    if (isTrackEditMode && editingTrackTourId) {
      libraryStore.ensureTourSelected(editingTrackTourId)
    }
  },
)

// ── Actions menu (… per row) ──────────────────────────────────────────────────

const openActionsMenuId = ref<string | null>(null)
const openOverflowTourId = ref<string | null>(null)
const actionsMenuStyle = ref<Record<string, string>>({})
const overflowMenuStyle = ref<Record<string, string>>({})

// Shared helper for 288px-wide dropdowns (add & go, manage collections).
// Flips upward when there isn't enough space below the anchor point.
const calcDropdownStyle = (
  anchorLeft: number,
  anchorY: number, // bottom of button or cursor Y
  anchorTopY?: number, // top of button — required for flip; omit for cursor-only calls
): Record<string, string> => {
  const MARGIN = 8
  const GAP = 4
  const DROPDOWN_W = 288
  const clampedLeft = Math.min(anchorLeft, window.innerWidth - DROPDOWN_W - MARGIN)
  const spaceBelow = window.innerHeight - anchorY - GAP - MARGIN
  const spaceAbove = anchorTopY !== undefined ? anchorTopY - GAP - MARGIN : 0
  const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow && anchorTopY !== undefined
  if (openUpward) {
    return {
      bottom: `${window.innerHeight - anchorTopY! + GAP}px`,
      left: `${clampedLeft}px`,
      width: `${DROPDOWN_W}px`,
      maxHeight: `${Math.max(200, spaceAbove)}px`,
    }
  }
  return {
    top: `${anchorY + GAP}px`,
    left: `${clampedLeft}px`,
    width: `${DROPDOWN_W}px`,
    maxHeight: `${Math.max(200, spaceBelow)}px`,
  }
}

const calcRowMenuStyle = (event: MouseEvent, menuWidthPx: number): Record<string, string> => {
  const btn = event.currentTarget as HTMLElement
  const rect = btn.getBoundingClientRect()
  const MARGIN = 8
  const GAP = 4
  const right = window.innerWidth - rect.right
  const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN
  const spaceAbove = rect.top - GAP - MARGIN
  // Flip upward if space below is less than 200px and there's more room above
  const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow
  if (openUpward) {
    return {
      bottom: `${window.innerHeight - rect.top + GAP}px`,
      right: `${right}px`,
      maxHeight: `${Math.max(120, spaceAbove)}px`,
      minWidth: `${menuWidthPx}px`,
    }
  }
  return {
    top: `${rect.bottom + GAP}px`,
    right: `${right}px`,
    maxHeight: `${Math.max(120, spaceBelow)}px`,
    minWidth: `${menuWidthPx}px`,
  }
}

const toggleActionsMenu = (tourId: string, event: MouseEvent) => {
  event.stopPropagation()
  if (openActionsMenuId.value === tourId) {
    openActionsMenuId.value = null
    return
  }
  actionsMenuStyle.value = calcRowMenuStyle(event, 192) // w-48
  openActionsMenuId.value = tourId
}

let overflowHoverTimer: ReturnType<typeof setTimeout> | null = null

const openOverflowHover = (tourId: string, event: MouseEvent) => {
  if (overflowHoverTimer) {
    clearTimeout(overflowHoverTimer)
    overflowHoverTimer = null
  }
  overflowMenuStyle.value = calcRowMenuStyle(event, 160)
  openOverflowTourId.value = tourId
}

const cancelOverflowHover = () => {
  if (overflowHoverTimer) {
    clearTimeout(overflowHoverTimer)
    overflowHoverTimer = null
  }
}

const closeOverflowHover = () => {
  overflowHoverTimer = setTimeout(() => {
    openOverflowTourId.value = null
    overflowHoverTimer = null
  }, 120)
}

const toggleOverflowPopover = (tourId: string, event: MouseEvent) => {
  if (openOverflowTourId.value === tourId) {
    openOverflowTourId.value = null
  } else {
    openOverflowHover(tourId, event)
  }
}

const closeActionsMenu = () => {
  openActionsMenuId.value = null
  openOverflowTourId.value = null
}

const handleTogglePinned = async (tour: TourListItem) => {
  closeActionsMenu()
  await toggleTourPinned(tour)
}

const handleActionsDocClick = () => {
  closeActionsMenu()
  if (!isSmallScreen.value && addToColOpen.value) closeAddToCol()
}

// ── Inline row expansion (desktop) ───────────────────────────────────────────

// Cache of full Tour objects keyed by clientId (populated on first expand)
const expandedRowDetails = reactive<Record<string, Tour>>({})
const expandedRowLoading = ref<Set<string>>(new Set())

const loadExpandedRowDetail = async (tourId: string) => {
  expandedRowLoading.value = new Set([...expandedRowLoading.value, tourId])
  try {
    const full = await apiService.getTourDetail(tourId)
    expandedRowDetails[tourId] = full
    emit('tour-detail-loaded', tourId)
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to load expanded row detail', { error, tourId })
  } finally {
    const next = new Set(expandedRowLoading.value)
    next.delete(tourId)
    expandedRowLoading.value = next
  }
}

// ── Add to Collection (inline, no navigation) ────────────────────────────────

const addToColOpen = ref(false)
const addToColTour = ref<TourListItem | null>(null)
const addToColSearch = ref('')

const closeAddToCol = () => {
  addToColOpen.value = false
  addToColTour.value = null
  addToColSearch.value = ''
}

// ── Expanded row — track menu state ──────────────────────────────────────────

const expandedTrackMenuIdx = ref<number | null>(null)
const expandedTrackDownloading = ref<Set<string>>(new Set())

// Download entire tour as GPX (used from context menu / ... menu)
const downloadingTourIds = ref<Set<string>>(new Set())

const downloadTourGpx = async (tour: TourListItem) => {
  if (downloadingTourIds.value.has(tour.clientId)) return
  downloadingTourIds.value = new Set([...downloadingTourIds.value, tour.clientId])
  closeContextMenu()
  closeActionsMenu()
  try {
    const blob = await apiService.downloadGpx(tour.clientId)
    const name = (tour.name ?? 'activity').trim()
    await downloadFile(blob, `${name}.gpx`, { mimeType: 'application/gpx+xml' })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download tour GPX', { error })
    showError('Failed to download GPX')
  } finally {
    const next = new Set(downloadingTourIds.value)
    next.delete(tour.clientId)
    downloadingTourIds.value = next
  }
}

const downloadExpandedTrack = async (track: Track, ti: number) => {
  if (!track.clientId) return
  expandedTrackMenuIdx.value = null
  expandedTrackDownloading.value = new Set([...expandedTrackDownloading.value, track.clientId])
  try {
    const blob = await apiService.downloadTrackGpx(track.clientId)
    const name = track.name?.trim() || `Track ${ti + 1}`
    await downloadFile(blob, `${name}.gpx`, { mimeType: 'application/gpx+xml' })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download track GPX', { error })
    showError('Failed to download track GPX')
  } finally {
    const next = new Set(expandedTrackDownloading.value)
    next.delete(track.clientId!)
    expandedTrackDownloading.value = next
  }
}

const deleteExpandedTrack = async (
  tourId: string,
  tour: TourListItem,
  track: Track,
  ti: number,
) => {
  expandedTrackMenuIdx.value = null
  const confirmed = await showConfirm({
    title: 'Delete Track',
    message: `Are you sure you want to delete "${track.name || `Track ${ti + 1}`}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })
  if (!confirmed) return
  try {
    const eventStore = useEventStore()
    await eventStore.addEvent({
      type: EventType.TRACK_DELETED,
      payload: { clientId: track.clientId },
    })
    const full = expandedRowDetails[tourId]
    if (full) {
      const idx = full.tracks.findIndex((t) => t.clientId === track.clientId)
      if (idx >= 0) full.tracks.splice(idx, 1)
    }
    const listItem = libraryStore.tours.find((t) => t.clientId === tourId)
    if (listItem) {
      const lIdx = listItem.tracks.findIndex((t) => t.clientId === track.clientId)
      if (lIdx >= 0) listItem.tracks.splice(lIdx, 1)
    }
    logService.info(LOG_CATEGORIES.UI, 'Deleted track from expanded row', {
      tourId,
      trackClientId: track.clientId,
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to delete track', { error })
    showError(`Failed to delete track: ${error instanceof Error ? error.message : String(error)}`)
  }
}

// ── Expanded row — tour stats helpers ────────────────────────────────────────

onUnmounted(() => document.removeEventListener('click', handleActionsDocClick))

const openRenameFromMenu = (tour: TourListItem) => {
  closeActionsMenu()
  if (isSmallScreen.value) {
    openSingleMobileRename(tour)
    return
  }
  renameTargetTours.value = [tour]
  renameValue.value = tour.name ?? ''
  renameDialogOpen.value = true
}

// ── Row context menu (mobile bottom sheet) ────────────────────────────────────

const contextMenuOpen = ref(false)
const contextMenuTour = ref<TourListItem | null>(null)
const contextMenuTourAccentColor = computed(() => resolveTourAccentColor(contextMenuTour.value))

const openRowContextMenu = (tour: TourListItem) => {
  contextMenuTour.value = tour
  contextMenuOpen.value = true
}

const closeContextMenu = () => {
  contextMenuOpen.value = false
}

const handleContextMenuClosed = () => {
  contextMenuTour.value = null
}

const handleContextMenuViewDetails = () => {
  const tour = contextMenuTour.value
  if (!tour) return
  closeContextMenu()
  emit('open-mobile-tour-detail', tour)
}

// ── Inline rename ─────────────────────────────────────────────────────────────

const renamingTourId = ref<string | null>(null)
const inlineRenameValue = ref('')
const inlineRenameInputRef = ref<HTMLInputElement | null>(null)
const openSingleMobileRename = (tour: TourListItem) => {
  openMobileRenameSheet({
    title: `Rename ${isTourActivity(tour) ? 'activity' : 'route'}`,
    currentName: tour.name ?? '',
    submit: async (name) => {
      if (name === tour.name) return
      await libraryStore.updateTour(tour.clientId, { name })
    },
  })
}

const startInlineRename = (tour: TourListItem) => {
  closeContextMenu()
  closeActionsMenu()
  if (isSmallScreen.value) {
    openSingleMobileRename(tour)
    return
  }
  renamingTourId.value = tour.clientId
  inlineRenameValue.value = tour.name ?? ''
}

// After the DOM updates, move focus to the real inline input and place the
// cursor at the end. flush:'post' ensures the element exists in the DOM.
watch(
  renamingTourId,
  (id) => {
    if (!id) return
    const el = Array.isArray(inlineRenameInputRef.value)
      ? inlineRenameInputRef.value[0]
      : inlineRenameInputRef.value
    if (el) {
      el.focus()
      const len = el.value.length
      el.setSelectionRange(len, len)
    }
  },
  { flush: 'post' },
)

const commitInlineRename = async () => {
  const tourId = renamingTourId.value
  if (!tourId) return
  const value = inlineRenameValue.value.trim()
  renamingTourId.value = null
  if (!value) return
  try {
    await libraryStore.updateTour(tourId, { name: value })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to rename tour', { error })
    showError(`Failed to rename: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const cancelInlineRename = () => {
  renamingTourId.value = null
  inlineRenameValue.value = ''
}

// ── Manage collections sheet ──────────────────────────────────────────────────

const manageColOpen = ref(false)
const manageColTour = ref<TourListItem | null>(null)
const manageColClosing = ref(false)
const manageColDragY = ref(0)
const manageColSearch = ref('')
const manageColProcessing = ref<Set<string>>(new Set())
const manageColDropdownStyle = ref<Record<string, string>>({})
let _manageDragStartY = 0
let _manageDragging = false

const manageColSheetStyle = computed(() => {
  if (manageColClosing.value) {
    return { transform: 'translateY(100%)', transition: 'transform 0.3s ease' }
  }
  if (manageColDragY.value > 0) {
    return { transform: `translateY(${manageColDragY.value}px)`, transition: 'none' }
  }
  return {}
})

const manageColFilteredWorkspaces = computed(() => {
  const q = manageColSearch.value.trim().toLowerCase()
  return searchWorkspaceStore.allWorkspaces.filter(
    (w) => (w.isOwner || w.canEdit === true) && (!q || w.name.toLowerCase().includes(q)),
  )
})

const isTourInWorkspace = (workspaceClientId: string): boolean =>
  manageColTour.value?.workspaces.some((w) => w.clientId === workspaceClientId) ?? false

const openManageCollections = (
  tour: TourListItem,
  eventOrPos?: MouseEvent | { x: number; y: number },
) => {
  closeContextMenu()
  closeActionsMenu()
  manageColTour.value = tour
  manageColOpen.value = true
  manageColClosing.value = false
  manageColDragY.value = 0
  manageColSearch.value = ''
  if (!isSmallScreen.value && eventOrPos) {
    if (eventOrPos instanceof MouseEvent) {
      const btn = eventOrPos.currentTarget as HTMLElement
      const rect = btn.getBoundingClientRect()
      manageColDropdownStyle.value = calcDropdownStyle(rect.left, rect.bottom, rect.top)
    } else {
      // Cursor-only position (from map right-click) — no top anchor for flip
      manageColDropdownStyle.value = calcDropdownStyle(eventOrPos.x, eventOrPos.y)
    }
  }
}

const closeManageCollections = () => {
  if (!isSmallScreen.value) {
    manageColOpen.value = false
    manageColTour.value = null
    manageColSearch.value = ''
    return
  }
  manageColClosing.value = true
  setTimeout(() => {
    manageColOpen.value = false
    manageColTour.value = null
    manageColClosing.value = false
    manageColDragY.value = 0
    manageColSearch.value = ''
  }, 300)
}

const toggleCollectionMembership = async (ws: { clientId: string; name: string }) => {
  const tour = manageColTour.value
  if (!tour || manageColProcessing.value.has(ws.clientId)) return
  const eventStore = useEventStore()
  const isIn = isTourInWorkspace(ws.clientId)
  manageColProcessing.value = new Set([...manageColProcessing.value, ws.clientId])
  try {
    if (isIn) {
      await eventStore.addEvent({
        type: EventType.TOUR_REMOVED_FROM_WORKSPACE,
        payload: { client_id: tour.clientId, workspace_client_id: ws.clientId },
      })
      const idx = tour.workspaces.findIndex((w) => w.clientId === ws.clientId)
      if (idx !== -1) tour.workspaces.splice(idx, 1)
    } else {
      await eventStore.addEvent({
        type: EventType.TOUR_ADDED_TO_WORKSPACE,
        payload: { client_id: tour.clientId, workspace_client_id: ws.clientId },
      })
      tour.workspaces.push({ clientId: ws.clientId, name: ws.name })
    }
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to toggle collection membership', { error })
    showError(`Failed: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    const next = new Set(manageColProcessing.value)
    next.delete(ws.clientId)
    manageColProcessing.value = next
  }
}

const onManageDragStart = (e: TouchEvent) => {
  _manageDragStartY = e.touches[0].clientY
  _manageDragging = true
  manageColDragY.value = 0
}

const onManageDragMove = (e: TouchEvent) => {
  if (!_manageDragging) return
  manageColDragY.value = Math.max(0, e.touches[0].clientY - _manageDragStartY)
}

const onManageDragEnd = () => {
  _manageDragging = false
  if (manageColDragY.value > 120) {
    manageColDragY.value = 0
    closeManageCollections()
  } else {
    manageColDragY.value = 0
  }
}

// Expose openManageCollections so parent components can trigger it (e.g. from map right-click)
const openManageCollectionsByTourId = (tourId: string, position?: { x: number; y: number }) => {
  const tour = sortedTours.value.find((t) => t.clientId === tourId)
  if (tour) openManageCollections(tour, position)
}

const openAddToCollectionForTours = (tours: TourListItem[], event?: MouseEvent) => {
  event?.stopPropagation()
  closeActionsMenu()
  closeContextMenu()
  openAddToCollectionPicker({ tours: tours.map(tourToAddToCollectionItem) })
}

const openBulkAddToCollection = (event?: MouseEvent) => {
  const selectedTours = sortedTours.value.filter((t) =>
    libraryStore.selectedTourIds.has(t.clientId),
  )
  if (selectedTours.length === 0) return
  openAddToCollectionForTours(selectedTours, event)
}

const openDeleteFromMenu = (tour: TourListItem) => {
  closeActionsMenu()
  closeContextMenu()
  singleDeleteTargetTour.value = tour
}

// ── Delete dialog ─────────────────────────────────────────────────────────────

const deleteDialogOpen = ref(false)
const deleteTargetTours = ref<TourListItem[]>([])
const singleDeleteTargetTour = ref<TourListItem | null>(null)
const isSingleDeleteDialogOpen = computed(() => singleDeleteTargetTour.value !== null)
const isDeleting = ref(false)

const closeDeleteDialog = () => {
  if (isDeleting.value) return
  deleteDialogOpen.value = false
  deleteTargetTours.value = []
}

const closeSingleDeleteDialog = () => {
  if (isDeleting.value) return
  singleDeleteTargetTour.value = null
}

const deleteTours = async (tours: TourListItem[]) => {
  if (tours.length === 0) return

  isDeleting.value = true
  try {
    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    workspaceStore._createEventPushed = true

    await eventStore.addEvents(
      tours.map((tour) => ({
        type: EventType.TOUR_DELETED,
        payload: { clientId: tour.clientId },
      })),
    )

    libraryStore.removeTours(tours.map((t) => t.clientId))

    const next = new Set(libraryStore.selectedTourIds)
    for (const tour of tours) {
      next.delete(tour.clientId)
    }
    libraryStore.setSelectedTourIds(next)

    logService.info(LOG_CATEGORIES.UI, 'Deleted tour(s)', {
      tourIds: tours.map((t) => t.clientId),
    })
    showSuccess(`Deleted ${tours.length} tour${tours.length !== 1 ? 's' : ''}`)
    deleteDialogOpen.value = false
    deleteTargetTours.value = []
    singleDeleteTargetTour.value = null
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to delete tour(s)', { error })
    showError(`Failed to delete: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    isDeleting.value = false
  }
}

const submitDelete = async () => {
  await deleteTours(deleteTargetTours.value)
}

const submitSingleDelete = async () => {
  const tour = singleDeleteTargetTour.value
  if (!tour) return
  await deleteTours([tour])
}

const getSelectedToursForBatchAction = () => {
  const selectedIds = libraryStore.selectedTourIds
  const sortedSelectedTours = sortedTours.value.filter((t) => selectedIds.has(t.clientId))
  if (sortedSelectedTours.length === selectedIds.size) return sortedSelectedTours

  const toursById = new Map(libraryStore.tours.map((tour) => [tour.clientId, tour]))
  return Array.from(selectedIds)
    .map((tourId) => toursById.get(tourId))
    .filter((tour): tour is TourListItem => tour !== undefined)
}

const openBulkDelete = () => {
  const tours = getSelectedToursForBatchAction()
  if (tours.length === 0) return
  if (tours.length === 1) {
    singleDeleteTargetTour.value = tours[0]
    return
  }
  deleteTargetTours.value = tours
  deleteDialogOpen.value = true
}

// ── Rename dialog ─────────────────────────────────────────────────────────────

const renameDialogOpen = ref(false)
const renameTargetTours = ref<TourListItem[]>([])
const renameValue = ref('')
const isRenaming = ref(false)

const closeRenameDialog = () => {
  renameDialogOpen.value = false
  renameTargetTours.value = []
  renameValue.value = ''
  isRenaming.value = false
}

const renamePreview = computed(() => {
  const template = renameValue.value.trim()
  const count = renameTargetTours.value.length
  if (!template || count <= 1 || !template.includes('{#}')) return null
  const first = resolveName(template, 1)
  const last = resolveName(template, count)
  return { first, last }
})

const resolveName = (template: string, index: number) => template.replace(/\{#\}/g, String(index))

const submitRename = async () => {
  const tours = renameTargetTours.value
  const template = renameValue.value.trim()
  if (!template || tours.length === 0) return

  isRenaming.value = true
  try {
    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    workspaceStore._createEventPushed = true

    await eventStore.addEvents(
      tours.map((tour, i) => ({
        type: EventType.TOUR_UPDATED,
        payload: {
          clientId: tour.clientId,
          changes: [{ field: 'name', value: resolveName(template, i + 1) }],
        },
      })),
    )

    for (let i = 0; i < tours.length; i++) {
      tours[i].name = resolveName(template, i + 1)
      libraryStore.markTourLocallyModified(tours[i].clientId)
    }

    logService.info(LOG_CATEGORIES.UI, 'Renamed tour(s)', {
      tourIds: tours.map((t) => t.clientId),
      template,
    })
    closeRenameDialog()
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to rename tour(s)', { error })
    showError(`Failed to rename: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    isRenaming.value = false
  }
}

// ── Bulk actions ──────────────────────────────────────────────────────────────

const openBulkRename = () => {
  const tours = sortedTours.value.filter((t) => libraryStore.selectedTourIds.has(t.clientId))
  if (tours.length === 0) return
  if (tours.length === 1 && isSmallScreen.value) {
    openSingleMobileRename(tours[0])
    return
  }
  renameTargetTours.value = tours
  renameValue.value = tours.length === 1 ? (tours[0].name ?? '') : ''
  renameDialogOpen.value = true
}

// ── Manage Collections dialog (single + bulk) ─────────────────────────────────

const wsDialogTours = ref<TourListItem[]>([])
const wsDialogOpen = ref(false)
const wsDialogSearch = ref('')
const isWsDialogProcessing = ref(false)
const wsDialogNewName = ref('')
const wsDialogIsCreating = ref(false)

const openWsDialog = (tours: TourListItem[]) => {
  wsDialogTours.value = tours
  wsDialogSearch.value = ''
  wsDialogOpen.value = true
}

const closeWsDialog = () => {
  wsDialogOpen.value = false
  wsDialogTours.value = []
  wsDialogSearch.value = ''
  isWsDialogProcessing.value = false
  wsDialogNewName.value = ''
  wsDialogIsCreating.value = false
}

const wsDialogCreateWorkspace = async () => {
  const name = wsDialogNewName.value.trim()
  if (!name || wsDialogTours.value.length === 0) return

  wsDialogIsCreating.value = true
  try {
    const workspaceClientId = generateId()
    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    workspaceStore._createEventPushed = true

    await eventStore.addEvent({
      type: EventType.WORKSPACE_CREATED,
      payload: {
        clientId: workspaceClientId,
        name,
        isPublic: false,
        markedForDeletion: false,
        deletionScheduledAt: null,
        canEdit: true,
      },
    })

    const toursToAssign = wsDialogTours.value.filter(
      (t) => !t.workspaces.some((w) => w.clientId === workspaceClientId),
    )
    if (toursToAssign.length > 0) {
      await eventStore.addEvents(
        toursToAssign.map((tour) => ({
          type: EventType.TOUR_ADDED_TO_WORKSPACE,
          payload: {
            client_id: tour.clientId,
            workspace_client_id: workspaceClientId,
          },
        })),
      )
      for (const tour of toursToAssign) {
        tour.workspaces.push({ clientId: workspaceClientId, name })
      }
    }

    searchWorkspaceStore.allWorkspaces.push({
      clientId: workspaceClientId,
      name,
      isPublic: false,
      isOwner: true,
      type: 0,
      tours: [],
    })

    wsDialogNewName.value = ''
    logService.info(LOG_CATEGORIES.UI, 'Created workspace from manage dialog', {
      workspaceClientId,
      name,
      tourCount: toursToAssign.length,
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to create workspace from manage dialog', { error })
    showError(
      `Failed to create workspace: ${error instanceof Error ? error.message : String(error)}`,
    )
  } finally {
    wsDialogIsCreating.value = false
  }
}

const wsDialogFilteredWorkspaces = computed(() => {
  const q = wsDialogSearch.value.toLowerCase().trim()
  let list = workspaces.value
  if (q) {
    list = list.filter((ws) => ws.name.toLowerCase().includes(q))
  }
  const assignedIds = new Set(
    wsDialogTours.value.flatMap((t) => t.workspaces.map((w) => w.clientId)),
  )
  const assigned = list.filter((ws) => assignedIds.has(ws.clientId))
  const unassigned = list.filter((ws) => !assignedIds.has(ws.clientId))
  const byName = (a: { name: string }, b: { name: string }) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  return [...assigned.sort(byName), ...unassigned.sort(byName)]
})

const wsAssignedCount = (wsClientId: string) =>
  wsDialogTours.value.filter((t) => t.workspaces.some((w) => w.clientId === wsClientId)).length

const bulkAssignWorkspace = async (wsClientId: string) => {
  const ws = workspaces.value.find((w) => w.clientId === wsClientId)
  if (!ws) return

  const toursToAssign = wsDialogTours.value.filter(
    (t) => !t.workspaces.some((w) => w.clientId === wsClientId),
  )
  if (toursToAssign.length === 0) return

  isWsDialogProcessing.value = true
  try {
    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    workspaceStore._createEventPushed = true

    await eventStore.addEvents(
      toursToAssign.map((tour) => ({
        type: EventType.TOUR_ADDED_TO_WORKSPACE,
        payload: {
          client_id: tour.clientId,
          workspace_client_id: ws.clientId,
        },
      })),
    )

    for (const tour of toursToAssign) {
      tour.workspaces.push({ clientId: ws.clientId, name: ws.name })
    }

    logService.info(LOG_CATEGORIES.UI, 'Bulk assigned workspace', {
      workspaceId: ws.clientId,
      tourCount: toursToAssign.length,
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to bulk assign workspace', { error })
    showError(
      `Failed to assign workspace: ${error instanceof Error ? error.message : String(error)}`,
    )
  } finally {
    isWsDialogProcessing.value = false
  }
}

const bulkRemoveWorkspace = async (wsClientId: string) => {
  const toursToRemove = wsDialogTours.value.filter((t) =>
    t.workspaces.some((w) => w.clientId === wsClientId),
  )
  if (toursToRemove.length === 0) return

  isWsDialogProcessing.value = true
  try {
    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    workspaceStore._createEventPushed = true

    await eventStore.addEvents(
      toursToRemove.map((tour) => ({
        type: EventType.TOUR_REMOVED_FROM_WORKSPACE,
        payload: {
          client_id: tour.clientId,
          workspace_client_id: wsClientId,
        },
      })),
    )

    for (const tour of toursToRemove) {
      tour.workspaces = tour.workspaces.filter((w) => w.clientId !== wsClientId)
    }

    logService.info(LOG_CATEGORIES.UI, 'Bulk removed workspace', {
      workspaceId: wsClientId,
      tourCount: toursToRemove.length,
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to bulk remove workspace', { error })
    showError(
      `Failed to remove workspace: ${error instanceof Error ? error.message : String(error)}`,
    )
  } finally {
    isWsDialogProcessing.value = false
  }
}

const openBulkManageWorkspaces = () => {
  const tours = sortedTours.value.filter((t) => libraryStore.selectedTourIds.has(t.clientId))
  if (tours.length === 0) return
  openWsDialog(tours)
}

const openEditForSelectedTour = () => {
  // No-op: editing is now handled inline in TourDetailPanel
}

defineExpose({
  exitSelectionMode,
  openEditForSelectedTour,
  openBulkRename,
  openBulkManageWorkspaces,
  openBulkAddToCollection,
  openBulkDelete,
  // Exposed for MobileToolbar to drive the in-panel tour detail view
  expandedRowDetails,
  expandedRowLoading,
  loadExpandedRowDetail,
  // Collection & track actions (called from mobile detail panel)
  removeFromWorkspace,
  downloadExpandedTrack,
  deleteExpandedTrack,
  openRenameFromMenu,
  // Context menu actions — callable from outside (e.g. map right-click)
  openManageCollectionsByTourId,
  openAddToCollectionByTourId: (tourId: string, position?: { x: number; y: number }) => {
    void position
    const tour = sortedTours.value.find((t) => t.clientId === tourId)
    if (!tour) return
    openAddToCollectionForTours([tour])
  },
})
</script>

<template>
  <div
    :class="
      isSmallScreen
        ? 'flex h-full flex-col overflow-hidden'
        : 'flex flex-1 min-h-0 flex-col overflow-hidden'
    "
    data-testid="all-activities-table"
  >
    <div data-testid="all-activities-top-controls" class="shrink-0">
      <!-- Filters -->
      <div
        data-testid="all-activities-toolbar"
        class="relative flex flex-col gap-2 border-b border-gray-200 py-2.5 bg-white px-3"
      >
        <!-- Row 1: Search -->
        <div class="flex items-center gap-2">
          <BaseSearchBar
            class="flex-1"
            :model-value="libraryStore.searchQuery"
            placeholder="Search Activities and Routes"
            @update:model-value="libraryStore.setSearch"
          />
        </div>

        <!-- Row 2: Filter chips + selection controls -->
        <div class="flex items-center gap-1">
          <!-- Chips (flex-1, scrolling on mobile / wrapping on desktop) -->
          <div class="relative flex-1 min-w-0">
            <div
              class="flex items-center gap-1 w-full"
              :class="isSmallScreen ? 'overflow-x-auto overflow-y-hidden -mx-3 px-3' : 'flex-wrap'"
              @touchstart.passive="onFilterBarTouchStart"
              @touchend.passive="onFilterBarTouchEnd"
              @touchcancel.passive="onFilterBarTouchEnd"
            >
              <!-- Recently added chip (always visible) -->
              <BaseFilterChip
                class="shrink-0"
                :style="{ order: libraryStore.showOnlyUploadedBatch ? 0 : 3 }"
                :active="libraryStore.showOnlyUploadedBatch"
                :disabled="libraryStore.uploadedBatchIds.size === 0"
                @click="
                  libraryStore.showOnlyUploadedBatch
                    ? libraryStore.clearUploadedBatchFilter()
                    : (libraryStore.showOnlyUploadedBatch = true)
                "
              >
                Recently added{{
                  !isSmallScreen && libraryStore.uploadedBatchIds.size > 0
                    ? ` (${libraryStore.uploadedBatchIds.size})`
                    : ''
                }}
              </BaseFilterChip>

              <!-- In view chip -->
              <BaseFilterChip
                class="shrink-0"
                :style="{ order: lockListToMap ? 0 : 1 }"
                :active="lockListToMap"
                @click="lockListToMap = !lockListToMap"
              >
                In view
              </BaseFilterChip>

              <!-- Type chip (dropdown) -->
              <div
                class="relative shrink-0"
                data-type-menu
                :style="{ order: typeFilter !== 'all' ? 0 : 1 }"
              >
                <BaseFilterChip
                  class="shrink-0"
                  :active="typeFilter !== 'all'"
                  data-type-menu
                  @click.stop="toggleTypeMenu($event)"
                >
                  <component
                    v-if="typeFilter !== 'all'"
                    :is="activeTypeIcon"
                    class="w-3 h-3 shrink-0"
                  />
                  {{ activeTypeLabel }}
                  <ChevronDown class="w-3 h-3 shrink-0" />
                </BaseFilterChip>
                <Teleport to="body">
                  <BaseMenu
                    v-if="typeMenuOpen"
                    class="w-44"
                    :style="typeMenuStyle"
                    data-type-menu
                    @click.stop
                  >
                    <BaseMenuItem
                      v-for="opt in typeOptions"
                      :key="opt.value"
                      :icon="opt.icon"
                      :selected="typeFilter === opt.value"
                      data-type-menu
                      @click.stop="selectTypeOption(opt.value)"
                    >
                      {{ opt.label }}
                    </BaseMenuItem>
                  </BaseMenu>
                </Teleport>
              </div>

              <!-- Collection dropdown chip -->
              <div
                class="relative shrink-0"
                data-collection-menu
                :style="{ order: collectionFilter !== 'all' ? 0 : 1 }"
              >
                <BaseFilterChip
                  class="shrink-0"
                  :active="collectionFilter !== 'all'"
                  data-collection-menu
                  @click.stop="toggleCollectionMenu($event)"
                >
                  {{ activeCollectionLabel }}
                  <ChevronDown class="w-3 h-3 shrink-0" />
                </BaseFilterChip>
                <Teleport to="body">
                  <BaseMenu
                    v-if="collectionMenuOpen"
                    class="w-52"
                    :style="collectionMenuStyle"
                    data-collection-menu
                    @click.stop
                  >
                    <!-- All + Unassigned options -->
                    <BaseMenuItem
                      :selected="collectionFilter === 'all'"
                      data-collection-menu
                      @click="selectCollectionOption('all')"
                    >
                      All
                    </BaseMenuItem>
                    <BaseMenuItem
                      :selected="collectionFilter === 'unassigned'"
                      data-collection-menu
                      @click="selectCollectionOption('unassigned')"
                    >
                      Unassigned
                    </BaseMenuItem>
                    <!-- Search input -->
                    <div class="px-3 py-1.5 border-t border-gray-100" data-collection-menu>
                      <input
                        v-model="collectionSearchQuery"
                        type="text"
                        placeholder="Search collections…"
                        class="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        data-collection-menu
                        @click.stop
                      />
                    </div>
                    <!-- Collection list -->
                    <div class="max-h-44 overflow-y-auto" data-collection-menu>
                      <BaseMenuItem
                        v-for="ws in filteredCollectionOptions"
                        :key="ws.clientId"
                        class="whitespace-nowrap"
                        :selected="collectionFilter === ws.clientId"
                        data-collection-menu
                        @click="selectCollectionOption(ws.clientId)"
                      >
                        {{ ws.name }}
                      </BaseMenuItem>
                    </div>
                  </BaseMenu>
                </Teleport>
              </div>

              <!-- Date dropdown chip -->
              <div
                class="relative shrink-0"
                data-date-menu
                :style="{ order: datePreset !== 'any' ? 0 : 1 }"
              >
                <BaseFilterChip
                  class="shrink-0"
                  :active="datePreset !== 'any'"
                  data-date-menu
                  @click.stop="toggleDateMenu($event)"
                >
                  {{ activeDateLabel }}
                  <ChevronDown class="w-3 h-3 shrink-0" />
                </BaseFilterChip>
                <Teleport to="body">
                  <BaseMenu
                    v-if="dateMenuOpen"
                    scroll
                    :style="dateMenuStyle"
                    data-date-menu
                    @click.stop
                  >
                    <BaseMenuItem
                      v-for="opt in datePresetOptions"
                      :key="opt.value"
                      class="whitespace-nowrap"
                      :selected="datePreset === opt.value"
                      data-date-menu
                      @click.stop="selectDateOption(opt.value)"
                    >
                      {{ opt.label }}
                    </BaseMenuItem>
                    <!-- Custom date pickers inline in dropdown -->
                    <div
                      v-if="datePreset === 'custom'"
                      ref="dateCustomSectionRef"
                      class="px-3 py-2 border-t border-gray-100 flex flex-col gap-2"
                    >
                      <div class="flex items-center gap-1.5">
                        <label class="text-[11px] text-gray-500 w-7 shrink-0">From</label>
                        <input
                          type="date"
                          :value="libraryStore.dateFrom ?? ''"
                          @change.stop="
                            libraryStore.setDateRange(
                              ($event.target as HTMLInputElement).value || null,
                              libraryStore.dateTo,
                            )
                          "
                          class="px-2 py-1 text-xs bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                      <div class="flex items-center gap-1.5">
                        <label class="text-[11px] text-gray-500 w-7 shrink-0">To</label>
                        <input
                          type="date"
                          :value="libraryStore.dateTo ?? ''"
                          @change.stop="
                            libraryStore.setDateRange(
                              libraryStore.dateFrom,
                              ($event.target as HTMLInputElement).value || null,
                            )
                          "
                          class="px-2 py-1 text-xs bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  </BaseMenu>
                </Teleport>
              </div>

              <!-- Distance dropdown chip -->
              <div
                class="relative shrink-0"
                data-distance-menu
                :style="{ order: distancePreset !== 'all' ? 0 : 1 }"
              >
                <BaseFilterChip
                  class="shrink-0"
                  :active="distancePreset !== 'all'"
                  data-distance-menu
                  @click.stop="toggleDistanceMenu($event)"
                >
                  {{ activeDistanceLabel }}
                  <ChevronDown class="w-3 h-3 shrink-0" />
                </BaseFilterChip>
                <Teleport to="body">
                  <BaseMenu
                    v-if="distanceMenuOpen"
                    scroll
                    :style="distanceMenuStyle"
                    data-distance-menu
                    @click.stop
                  >
                    <BaseMenuItem
                      v-for="opt in distanceOptions"
                      :key="opt.value"
                      class="whitespace-nowrap"
                      :selected="distancePreset === opt.value"
                      data-distance-menu
                      @click="selectDistanceOption(opt.value)"
                    >
                      {{ opt.label }}
                    </BaseMenuItem>
                    <!-- Custom range slider inline in dropdown -->
                    <div
                      v-if="distancePreset === 'custom' && tourMaxDistanceKm > 0"
                      class="px-3 py-2 border-t border-gray-100"
                    >
                      <div class="text-[11px] text-gray-500 mb-1.5">
                        {{ distanceFilterMin }}–{{ distanceFilterMax }} km
                      </div>
                      <div class="relative h-5 flex items-center w-44">
                        <div
                          class="absolute inset-x-0 h-1.5 rounded-full bg-gray-200 pointer-events-none"
                        />
                        <div
                          class="absolute h-1.5 rounded-full bg-emerald-400 pointer-events-none"
                          :style="distanceTrackStyle"
                        />
                        <input
                          type="range"
                          :min="0"
                          :max="tourMaxDistanceKm"
                          v-model.number="distanceFilterMin"
                          class="arc-slider"
                        />
                        <input
                          type="range"
                          :min="0"
                          :max="tourMaxDistanceKm"
                          v-model.number="distanceFilterMax"
                          class="arc-slider"
                        />
                      </div>
                    </div>
                  </BaseMenu>
                </Teleport>
              </div>

              <!-- Reset filters button — visible when any non-persistent filter is active -->
              <button
                v-if="hasActiveFilters"
                style="order: 4"
                @click="resetLocalFilters"
                class="shrink-0 whitespace-nowrap text-xs text-emerald-700 hover:text-emerald-900 hover:underline cursor-pointer transition-colors px-1"
              >
                Reset filters
              </button>
            </div>
            <!-- Fade-out gradient on right edge (mobile only) -->
            <div
              v-if="isSmallScreen"
              class="absolute right-0 top-0 bottom-0 w-8 pointer-events-none"
              style="background: linear-gradient(to right, transparent, white)"
            />
          </div>

          <!-- Sort icon button — fixed to the right of the filter bar -->
          <div class="relative shrink-0" data-sort-menu>
            <div
              v-if="isSmallScreen"
              class="absolute -left-1 top-1 bottom-1 w-px bg-gray-200 pointer-events-none"
            />
            <button
              @click.stop="toggleSortMenu($event)"
              class="flex items-center justify-center w-8 h-8 rounded-full cursor-pointer transition-colors"
              :class="isSortActive ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'"
              :title="activeSortChipLabel"
              data-sort-menu
            >
              <ArrowUpDown class="w-4 h-4" />
            </button>
            <Teleport to="body">
              <BaseMenu
                v-if="sortMenuOpen"
                scroll
                class="w-52"
                :style="sortMenuStyle"
                data-sort-menu
                @click.stop
              >
                <BaseMenuItem
                  v-for="opt in sortOptions"
                  :key="opt.value"
                  class="whitespace-nowrap"
                  :selected="activeSortKey === opt.value"
                  data-sort-menu
                  @click.stop="selectSortOption(opt.value)"
                >
                  {{ opt.label }}
                </BaseMenuItem>
              </BaseMenu>
            </Teleport>
          </div>
        </div>
        <div
          v-if="showLegacyMobileSelectionOverlay && isSmallScreen && libraryStore.isSelectionMode"
          class="absolute inset-0 bg-white z-10 flex flex-col justify-center px-3 gap-2"
        >
          <!-- Row 1: exit + count + action buttons -->
          <div class="flex items-center gap-1">
            <button
              @click="exitSelectionMode"
              class="flex items-center justify-center w-7 h-7 rounded text-gray-500 hover:bg-gray-100 transition-colors shrink-0"
              aria-label="Exit selection mode"
            >
              <X class="w-4 h-4" />
            </button>
            <span class="text-[13px] font-medium text-gray-900 mr-auto">
              {{ libraryStore.selectedTourIds.size }} selected
            </span>
            <!-- Action buttons -->
            <button
              @click="openBulkRename"
              :disabled="libraryStore.selectedTourIds.size === 0"
              class="flex items-center justify-center w-8 h-8 rounded transition-colors"
              :class="
                libraryStore.selectedTourIds.size === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer'
              "
              title="Rename"
            >
              <Pencil class="w-4 h-4" />
            </button>
            <button
              @click="openBulkAddToCollection($event)"
              :disabled="libraryStore.selectedTourIds.size === 0"
              class="flex items-center justify-center w-8 h-8 rounded transition-colors"
              :class="
                libraryStore.selectedTourIds.size === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer'
              "
              title="Add to collection"
            >
              <SquarePlus class="w-4 h-4" />
            </button>
            <button
              @click="openBulkDelete"
              :disabled="libraryStore.selectedTourIds.size === 0"
              class="flex items-center justify-center w-8 h-8 rounded transition-colors"
              :class="
                libraryStore.selectedTourIds.size === 0
                  ? 'text-red-200 cursor-not-allowed'
                  : 'text-red-500 hover:text-red-600 hover:bg-red-50 hover:cursor-pointer'
              "
              title="Delete"
            >
              <Trash2 class="w-4 h-4" />
            </button>
          </div>
          <!-- Row 2: select all / clear selection -->
          <button
            @click="toggleSelectAll"
            class="text-[13px] font-medium text-primary-600 hover:text-primary-700 text-left cursor-pointer"
          >
            {{ isAllSelected ? 'Clear selection' : 'Select all' }}
          </button>
        </div>
      </div>

      <input
        ref="fileInputRef"
        type="file"
        :accept="trackFilePickerAccept"
        multiple
        class="hidden"
        @change="handleFileInput"
      />
    </div>

    <!-- Mobile list view -->
    <div
      v-if="isSmallScreen"
      data-mobile-panel-scroll
      class="flex-1 min-h-0 overflow-auto"
      ref="mobileListRef"
      @scroll="onListScroll"
    >
      <!-- Loading -->
      <div v-if="libraryStore.isLoading" class="px-3 py-6 text-center text-gray-400">
        <div class="flex justify-center items-center gap-2">
          <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Loading…
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="libraryStore.hasLoaded && sortedTours.length === 0"
        class="px-3 py-8 text-center"
      >
        <template v-if="libraryStore.tours.length === 0">
          <p class="text-gray-500 font-medium">No Activities or Routes yet.</p>
          <p class="mt-1 text-sm text-gray-400">
            Upload a GPX or FIT file to get started —
            <button
              class="text-primary-600 hover:underline hover:cursor-pointer"
              @click="fileInputRef?.click()"
            >
              upload now
            </button>
          </p>
        </template>
        <template v-else>
          <p class="text-gray-500 font-medium">No Activities or Routes match these filters.</p>
          <p class="mt-1 text-sm text-gray-400">Try adjusting or clearing the filters above.</p>
        </template>
      </div>

      <!-- List -->
      <ul v-else>
        <li
          v-for="tour in sortedTours"
          :key="tour.clientId"
          :data-tour-id="tour.clientId"
          class="relative overflow-hidden border-b border-gray-100 last:border-0 cursor-pointer"
          @click="handleTourClick(tour, $event)"
        >
          <!-- Touch swipe background: pin/unpin action on either side. -->
          <div
            class="absolute inset-0 flex items-center justify-between border-y border-amber-200/70 bg-amber-500 px-5 text-white"
            aria-hidden="true"
          >
            <div class="flex items-center gap-3">
              <component :is="tour.isPinned ? PinOff : Pin" class="w-5 h-5 shrink-0" />
              <span class="text-xs font-semibold leading-tight">
                {{ tour.isPinned ? 'Unpin' : 'Pin' }}
              </span>
            </div>
            <div class="flex items-center gap-3">
              <span class="text-xs font-semibold leading-tight">
                {{ tour.isPinned ? 'Unpin' : 'Pin' }}
              </span>
              <component :is="tour.isPinned ? PinOff : Pin" class="w-5 h-5 shrink-0" />
            </div>
          </div>

          <!-- Sliding content row -->
          <div
            :style="{
              transform: `translateX(${tourSwipe.translateX[tour.clientId] ?? 0}px)`,
              transition: tourSwipe.transitioning[tour.clientId] ? 'transform 0.25s ease' : 'none',
            }"
            :class="[
              'relative flex items-center px-3 py-2.5 transition-colors',
              libraryStore.hoveredTourId === tour.clientId ? 'bg-stone-100' : 'bg-white',
            ]"
            @touchstart="onRowTouchStart($event, tour)"
            @touchmove="tourSwipe.move($event, tour)"
            @touchend="onRowTouchEnd(tour)"
            @touchcancel="onRowTouchCancel(tour)"
            @transitionend="tourSwipe.clearTransition(tour)"
          >
            <!-- Checkbox (mobile: only shown in selection mode) -->
            <BaseCheckbox
              v-show="libraryStore.isSelectionMode"
              class="mr-3 shrink-0"
              :model-value="libraryStore.selectedTourIds.has(tour.clientId)"
              @update:model-value="toggleSelect(tour.clientId)"
              @click.stop
            />

            <!-- Type icon + Name + meta -->
            <component
              :is="getTourTypeIcon(tour)"
              class="w-3.5 h-3.5 shrink-0 text-gray-400 self-center mr-2.5"
            />
            <div
              class="flex-1 min-w-0"
              @mouseenter="onListRowMouseEnter(tour.clientId)"
              @mouseleave="onListRowMouseLeave()"
            >
              <!-- line 1: name + collection pills -->
              <div class="flex items-center gap-1.5 min-w-0">
                <template v-if="renamingTourId === tour.clientId">
                  <input
                    ref="inlineRenameInputRef"
                    v-model="inlineRenameValue"
                    type="text"
                    class="text-[13px] font-medium text-gray-900 min-w-0 shrink bg-transparent border-b border-primary-400 outline-none"
                    @keyup.enter="commitInlineRename"
                    @keyup.escape.stop="cancelInlineRename"
                    @blur="commitInlineRename"
                    @click.stop
                    @touchstart.stop
                    @touchmove.stop
                    @touchend.stop
                  />
                </template>
                <template v-else>
                  <span
                    class="text-[13px] font-medium text-gray-900 truncate shrink min-w-15"
                    :title="tour.name ?? undefined"
                    >{{ tour.name ?? '—' }}</span
                  >
                </template>
                <!-- Pills wrapper -->
                <div
                  v-if="tour.workspaces.length > 0"
                  class="relative flex shrink min-w-0"
                  :data-pills-wrapper="tour.clientId"
                  :ref="(el) => registerPillsContainer(el as Element | null, tour.clientId)"
                >
                  <!-- Invisible probe: all pills at natural size for measurement -->
                  <div
                    data-pills-probe
                    class="absolute inset-0 flex gap-1.5 invisible pointer-events-none overflow-hidden"
                  >
                    <span
                      v-for="ws in tour.workspaces"
                      :key="ws.clientId + '-probe'"
                      data-pill-item
                      class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium shrink-0 whitespace-nowrap"
                      >{{ ws.name }}<span class="ml-0.5 w-2.5 h-2.5 inline-block shrink-0"
                    /></span>
                  </div>
                  <!-- Visible: only fully-fitting pills -->
                  <div class="flex gap-1.5">
                    <BaseRemovableTag
                      v-for="ws in tour.workspaces.slice(
                        0,
                        pillsVisibleCounts[tour.clientId] ?? tour.workspaces.length,
                      )"
                      :key="ws.clientId"
                      :label="ws.name"
                      :remove-label="`Remove ${ws.name} from ${tour.name}`"
                      @select="emit('select-workspace', ws.clientId, ws.name)"
                      @remove="removeFromWorkspace(tour, ws.clientId)"
                    />
                  </div>
                </div>
                <!-- +N overflow button -->
                <button
                  v-if="
                    tour.workspaces.length >
                    (pillsVisibleCounts[tour.clientId] ?? tour.workspaces.length)
                  "
                  @mouseenter="openOverflowHover(tour.clientId, $event)"
                  @mouseleave="closeOverflowHover()"
                  @click.stop="toggleOverflowPopover(tour.clientId, $event)"
                  class="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium whitespace-nowrap hover:bg-emerald-100 transition-colors cursor-default shrink-0"
                >
                  {{ (pillsVisibleCounts[tour.clientId] ?? tour.workspaces.length) === 0 ? '' : '+'
                  }}{{
                    tour.workspaces.length -
                    (pillsVisibleCounts[tour.clientId] ?? tour.workspaces.length)
                  }}
                </button>
                <!-- Overflow popover -->
                <Teleport to="body">
                  <div
                    v-if="openOverflowTourId === tour.clientId"
                    :style="overflowMenuStyle"
                    class="fixed z-9999 bg-white border border-gray-200 rounded-md shadow-lg py-1 overflow-y-auto"
                    @mouseenter="cancelOverflowHover()"
                    @mouseleave="closeOverflowHover()"
                    @click.stop
                  >
                    <div
                      v-for="ws in tour.workspaces.slice(pillsVisibleCounts[tour.clientId] ?? 0)"
                      :key="ws.clientId"
                      class="flex items-center gap-2 px-2.5 py-1 hover:bg-gray-50"
                    >
                      <button
                        class="flex-1 text-left text-[12px] text-emerald-700 hover:underline cursor-pointer"
                        :title="ws.name"
                        @click.stop="
                          (emit('select-workspace', ws.clientId, ws.name),
                          (openOverflowTourId = null))
                        "
                      >
                        {{ ws.name }}
                      </button>
                      <button
                        @click.stop="removeFromWorkspace(tour, ws.clientId)"
                        class="text-emerald-400 hover:text-red-500 transition-colors cursor-pointer rounded shrink-0"
                        :aria-label="`Remove ${ws.name} from ${tour.name}`"
                      >
                        <X class="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                </Teleport>
              </div>
              <!-- line 2: stats -->
              <div class="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                <span v-if="tour.startDate">{{ formatDate(tour.startDate) }}</span>
                <template v-if="formatDuration(tour.startDate, tour.endDate)">
                  <span class="text-gray-300">·</span>
                  <span>{{ formatDuration(tour.startDate, tour.endDate) }}</span>
                </template>
                <span
                  v-if="
                    (tour.startDate || formatDuration(tour.startDate, tour.endDate)) &&
                    tour.distance
                  "
                  class="text-gray-300"
                  >·</span
                >
                <span v-if="tour.distance">{{ formatDistance(tour.distance) }}</span>
                <span
                  v-if="(tour.startDate || tour.distance) && getVisibleTourElevationGain(tour)"
                  class="text-gray-300"
                  >·</span
                >
                <span v-if="getVisibleTourElevationGain(tour)">{{
                  formatElevation(getVisibleTourElevationGain(tour) ?? 0)
                }}</span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-0.5 shrink-0 ml-2 relative">
              <button
                @click.stop="handleTogglePinned(tour)"
                class="p-1.5 rounded transition-colors cursor-pointer"
                :class="
                  tour.isPinned
                    ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                    : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                "
                :aria-label="tour.isPinned ? `Remove ${tour.name} from Pinned` : `Pin ${tour.name}`"
                :title="tour.isPinned ? 'Remove from Pinned' : 'Pin'"
              >
                <Pin class="w-4 h-4" :class="{ 'fill-current': tour.isPinned }" />
              </button>
              <!-- Mobile: info button navigates to detail view -->
              <button
                v-if="isSmallScreen"
                @click.stop="emit('open-mobile-tour-detail', tour)"
                class="p-1.5 rounded text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                :aria-label="`View details for ${tour.name}`"
                title="View details"
              >
                <Info class="w-4 h-4" />
              </button>
              <button
                v-if="!isSmallScreen"
                @click.stop="handleInfoClick($event, tour)"
                class="p-1 rounded text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                :aria-label="`Open detail for ${tour.name}`"
                title="Open detail"
              >
                <Info class="w-4 h-4" />
              </button>
              <button
                v-if="!isSmallScreen"
                @click.stop="toggleActionsMenu(tour.clientId, $event)"
                class="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                :aria-label="`Actions for ${tour.name}`"
              >
                <EllipsisVertical class="w-4 h-4" />
              </button>

              <Teleport to="body">
                <div
                  v-if="openActionsMenuId === tour.clientId"
                  :style="actionsMenuStyle"
                  class="fixed z-9999 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden overflow-y-auto"
                  role="menu"
                  :aria-label="`Actions for ${tour.name}`"
                  @click.stop
                >
                  <button
                    @click="startInlineRename(tour)"
                    :aria-label="`Rename ${tour.name}`"
                    class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-50 text-left cursor-pointer"
                  >
                    <Pencil class="w-3.5 h-3.5 text-gray-400 shrink-0" /> rename
                  </button>
                  <button
                    @click="void downloadTourGpx(tour)"
                    :aria-label="`Download GPX for ${tour.name}`"
                    class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-50 text-left cursor-pointer"
                  >
                    <Download class="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span>download GPX</span>
                    <Loader2
                      v-if="downloadingTourIds.has(tour.clientId)"
                      class="w-3 h-3 ml-auto animate-spin text-gray-400 shrink-0"
                    />
                  </button>
                  <button
                    @click="openManageCollections(tour, $event)"
                    :aria-label="`Manage collections for ${tour.name}`"
                    class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-50 text-left cursor-pointer"
                  >
                    <FolderCog class="w-3.5 h-3.5 text-gray-400 shrink-0" /> manage collections
                  </button>
                  <button
                    @click="handleTogglePinned(tour)"
                    :aria-label="
                      tour.isPinned ? `Remove ${tour.name} from Pinned` : `Pin ${tour.name}`
                    "
                    class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-50 text-left cursor-pointer"
                  >
                    <Pin
                      class="w-3.5 h-3.5 shrink-0"
                      :class="tour.isPinned ? 'fill-current text-amber-600' : 'text-gray-400'"
                    />
                    {{ tour.isPinned ? 'remove from Pinned' : 'pin' }}
                  </button>
                  <button
                    @click="openDeleteFromMenu(tour)"
                    :aria-label="`Delete ${tour.name}`"
                    class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-red-600 transition-colors hover:bg-red-50 text-left cursor-pointer"
                  >
                    <Trash2 class="w-3.5 h-3.5 text-red-400 shrink-0" /> delete
                  </button>
                </div>
              </Teleport>
            </div>
          </div>
          <!-- end sliding content -->
        </li>
      </ul>
    </div>

    <!-- Table (desktop-style, single scrollable body) -->
    <div v-else class="flex-1 min-h-0 flex flex-col overflow-hidden">
      <!-- Sticky header: checkbox + count, with selection overlay -->
      <div class="relative shrink-0 bg-white border-b border-gray-200">
        <div class="flex items-center px-3 py-2">
          <div class="w-10 shrink-0 flex items-center">
            <BaseCheckbox
              :model-value="isAllSelected"
              :indeterminate="isSomeSelected"
              @update:model-value="toggleSelectAll"
            />
          </div>
          <span class="text-[13px] text-gray-700">
            {{ tourCountLabel }} activities &amp; routes
          </span>
        </div>
        <!-- Selection overlay: covers the count row when any items are selected -->
        <div
          v-if="libraryStore.selectedTourIds.size > 0"
          class="absolute inset-0 bg-white flex items-center px-3"
        >
          <div class="w-10 shrink-0 flex items-center">
            <BaseCheckbox
              :model-value="isAllSelected"
              :indeterminate="isSomeSelected"
              @update:model-value="toggleSelectAll"
            />
          </div>
          <span class="text-[13px] text-gray-900 shrink-0">
            {{ libraryStore.selectedTourIds.size }} selected
          </span>
          <span
            v-if="selectedDistance != null"
            class="text-[13px] text-gray-400 tabular-nums ml-2 shrink-0"
          >
            {{ formatDistanceSum(selectedDistance) }}
          </span>
          <span
            v-if="selectedElevationGain != null"
            class="text-[13px] text-gray-400 tabular-nums ml-1 shrink-0"
          >
            ↑ {{ formatElevationSum(selectedElevationGain) }}
          </span>
          <div class="flex items-center gap-1 ml-auto">
            <button
              @click="openBulkRename"
              class="flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              title="Rename"
            >
              <Pencil class="w-4 h-4" />
            </button>
            <button
              @click="openBulkAddToCollection($event)"
              class="flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              title="Add to collection"
            >
              <SquarePlus class="w-4 h-4" />
            </button>
            <button
              @click="openBulkDelete"
              class="flex h-8 w-8 items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
              title="Delete"
            >
              <Trash2 class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      <!-- Scrollable table body -->
      <div
        class="flex-1 min-h-0 overflow-y-auto overflow-x-clip"
        data-testid="all-activities-table-scroller"
        ref="desktopScrollerRef"
        @scroll="onListScroll"
      >
        <table class="w-full text-sm border-collapse table-fixed">
          <colgroup>
            <col class="w-10" />
            <!-- checkbox -->
            <col />
            <!-- name + collection + meta (flexible) -->
            <col class="w-28" />
            <!-- info + actions -->
          </colgroup>
          <tbody class="divide-y divide-gray-100">
            <!-- Loading -->
            <tr v-if="libraryStore.isLoading">
              <td :colspan="columns.length + 2" class="px-3 py-6 text-center text-gray-400">
                <div class="flex justify-center items-center gap-2">
                  <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Loading…
                </div>
              </td>
            </tr>

            <!-- Empty state -->
            <tr v-else-if="libraryStore.hasLoaded && sortedTours.length === 0">
              <td :colspan="columns.length + 2" class="px-3 py-8 text-center">
                <template v-if="libraryStore.tours.length === 0">
                  <p class="text-gray-500 font-medium">No Activities or Routes yet.</p>
                  <p class="mt-1 text-sm text-gray-400">
                    Upload a GPX or FIT file to get started —
                    <button
                      class="text-primary-600 hover:underline hover:cursor-pointer"
                      @click="fileInputRef?.click()"
                    >
                      upload now
                    </button>
                  </p>
                </template>
                <template v-else>
                  <p class="text-gray-500 font-medium">
                    No Activities or Routes match these filters.
                  </p>
                  <p class="mt-1 text-sm text-gray-400">
                    Try adjusting or clearing the filters above.
                  </p>
                </template>
              </td>
            </tr>

            <!-- Data rows -->
            <tr
              v-else
              v-for="tour in sortedTours"
              :key="tour.clientId"
              :data-tour-id="tour.clientId"
              :class="
                libraryStore.hoveredTourId === tour.clientId
                  ? 'bg-stone-100'
                  : 'bg-white hover:bg-stone-50'
              "
              @click="handleTourClick(tour)"
            >
              <td class="pl-2 pr-0 py-2 w-8">
                <BaseCheckbox
                  :model-value="libraryStore.selectedTourIds.has(tour.clientId)"
                  :disabled="
                    uiStore.isTrackEditMode && uiStore.editingTrackTourId === tour.clientId
                  "
                  @update:model-value="toggleSelect(tour.clientId)"
                  @click.stop
                />
              </td>

              <!-- Name + collection + meta -->
              <td
                class="pl-1 pr-0 py-2 min-w-0 cursor-pointer relative"
                @mouseenter="onListRowMouseEnter(tour.clientId)"
                @mouseleave="onListRowMouseLeave()"
              >
                <div class="flex items-center gap-1.5 min-w-0">
                  <!-- icon: centered vertically across both lines -->
                  <component
                    :is="getTourTypeIcon(tour)"
                    class="w-3.5 h-3.5 shrink-0 text-gray-400 self-center"
                  />
                  <!-- two-line content -->
                  <div class="flex-1 min-w-0">
                    <!-- line 1: name + collection pills -->
                    <div class="flex items-center gap-1.5 min-w-0">
                      <!-- Name: CSS truncation, shrinks proportionally when collections are present -->
                      <template v-if="renamingTourId === tour.clientId">
                        <input
                          ref="inlineRenameInputRef"
                          v-model="inlineRenameValue"
                          type="text"
                          class="text-[13px] font-medium text-gray-900 min-w-0 shrink bg-transparent border-b border-primary-400 outline-none"
                          @keyup.enter="commitInlineRename"
                          @keyup.escape.stop="cancelInlineRename"
                          @blur="commitInlineRename"
                          @click.stop
                        />
                      </template>
                      <template v-else>
                        <span
                          class="text-[13px] font-medium text-gray-900 hover:underline truncate shrink min-w-15"
                          :title="tour.name ?? undefined"
                          >{{ tour.name ?? '—' }}</span
                        >
                      </template>
                      <!-- Pills wrapper: relative so probe can fill it absolutely for measuring -->
                      <div
                        v-if="tour.workspaces.length > 0"
                        class="relative flex shrink min-w-0"
                        :data-pills-wrapper="tour.clientId"
                        :ref="(el) => registerPillsContainer(el as Element | null, tour.clientId)"
                      >
                        <!-- Invisible probe: ALL pills rendered here at natural size for measurement -->
                        <div
                          data-pills-probe
                          class="absolute inset-0 flex gap-1.5 invisible pointer-events-none overflow-hidden"
                        >
                          <span
                            v-for="ws in tour.workspaces"
                            :key="ws.clientId + '-probe'"
                            data-pill-item
                            class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium shrink-0 whitespace-nowrap"
                            >{{ ws.name }}<span class="ml-0.5 w-2.5 h-2.5 inline-block shrink-0"
                          /></span>
                        </div>
                        <!-- Visible: only fully-fitting pills -->
                        <div class="flex gap-1.5">
                          <BaseRemovableTag
                            v-for="ws in tour.workspaces.slice(
                              0,
                              pillsVisibleCounts[tour.clientId] ?? tour.workspaces.length,
                            )"
                            :key="ws.clientId"
                            :label="ws.name"
                            :remove-label="`Remove ${ws.name} from ${tour.name}`"
                            @select="emit('select-workspace', ws.clientId, ws.name)"
                            @remove="removeFromWorkspace(tour, ws.clientId)"
                          />
                        </div>
                      </div>
                      <!-- +N overflow pill (outside clipping container, always sized correctly) -->
                      <button
                        v-if="
                          tour.workspaces.length >
                          (pillsVisibleCounts[tour.clientId] ?? tour.workspaces.length)
                        "
                        @mouseenter="openOverflowHover(tour.clientId, $event)"
                        @mouseleave="closeOverflowHover()"
                        @click.stop="toggleOverflowPopover(tour.clientId, $event)"
                        class="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium whitespace-nowrap hover:bg-emerald-100 transition-colors cursor-default shrink-0"
                      >
                        {{
                          (pillsVisibleCounts[tour.clientId] ?? tour.workspaces.length) === 0
                            ? ''
                            : '+'
                        }}{{
                          tour.workspaces.length -
                          (pillsVisibleCounts[tour.clientId] ?? tour.workspaces.length)
                        }}
                      </button>
                      <!-- Overflow popover: collections not visible inline -->
                      <Teleport to="body">
                        <div
                          v-if="openOverflowTourId === tour.clientId"
                          :style="overflowMenuStyle"
                          class="fixed z-9999 bg-white border border-gray-200 rounded-md shadow-lg py-1 overflow-y-auto"
                          @mouseenter="cancelOverflowHover()"
                          @mouseleave="closeOverflowHover()"
                          @click.stop
                        >
                          <div
                            v-for="ws in tour.workspaces.slice(
                              pillsVisibleCounts[tour.clientId] ?? 0,
                            )"
                            :key="ws.clientId"
                            class="flex items-center gap-2 px-2.5 py-1 hover:bg-gray-50"
                          >
                            <button
                              class="flex-1 text-left text-[12px] text-emerald-700 hover:underline cursor-pointer"
                              :title="ws.name"
                              @click.stop="
                                (emit('select-workspace', ws.clientId, ws.name),
                                (openOverflowTourId = null))
                              "
                            >
                              {{ ws.name }}
                            </button>
                            <button
                              @click.stop="removeFromWorkspace(tour, ws.clientId)"
                              class="text-emerald-400 hover:text-red-500 transition-colors cursor-pointer rounded shrink-0"
                              :aria-label="`Remove ${ws.name} from ${tour.name}`"
                            >
                              <X class="w-2.5 h-2.5" />
                            </button>
                          </div>
                        </div>
                      </Teleport>
                    </div>
                    <div class="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                      <span
                        v-if="tour.startDate"
                        class="hover:underline cursor-pointer"
                        @click.stop="libraryStore.setDateRange(tour.startDate!.slice(0, 10), null)"
                        >{{ formatDate(tour.startDate) }}</span
                      >
                      <span v-else-if="tour.createdAt">{{ formatDate(tour.createdAt) }}</span>
                      <template v-if="formatDuration(tour.startDate, tour.endDate)">
                        <span class="text-gray-300">·</span>
                        <span>{{ formatDuration(tour.startDate, tour.endDate) }}</span>
                      </template>
                      <span
                        v-if="
                          (tour.startDate ||
                            tour.createdAt ||
                            formatDuration(tour.startDate, tour.endDate)) &&
                          tour.distance
                        "
                        class="text-gray-300"
                        >·</span
                      >
                      <span v-if="tour.distance">{{ formatDistance(tour.distance) }}</span>
                      <span
                        v-if="
                          (tour.startDate || tour.createdAt || tour.distance) &&
                          getVisibleTourElevationGain(tour)
                        "
                        class="text-gray-300"
                        >·</span
                      >
                      <span v-if="getVisibleTourElevationGain(tour)">{{
                        formatElevation(getVisibleTourElevationGain(tour) ?? 0)
                      }}</span>
                    </div>
                  </div>
                </div>
              </td>

              <!-- Info + Actions -->
              <td class="pl-1 pr-2 py-2" @click.stop>
                <div class="flex items-center justify-end">
                  <button
                    @click.stop="handleTogglePinned(tour)"
                    class="flex items-center justify-center w-7 h-7 rounded transition-colors cursor-pointer"
                    :class="
                      tour.isPinned
                        ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                        : 'text-gray-600 hover:text-amber-600 hover:bg-amber-50'
                    "
                    :aria-label="
                      tour.isPinned ? `Remove ${tour.name} from Pinned` : `Pin ${tour.name}`
                    "
                    :title="tour.isPinned ? 'Remove from Pinned' : 'Pin'"
                  >
                    <Pin class="w-4 h-4" :class="{ 'fill-current': tour.isPinned }" />
                  </button>
                  <button
                    @click.stop="openAddToCollectionForTours([tour], $event)"
                    class="flex items-center justify-center w-7 h-7 rounded transition-colors cursor-pointer text-gray-600 hover:text-emerald-600 hover:bg-emerald-50"
                    :aria-label="`Add ${tour.name} to a collection`"
                    title="Add to collection"
                  >
                    <SquarePlus class="w-4 h-4" />
                  </button>
                  <button
                    @click.stop="handleInfoClick($event, tour)"
                    class="flex items-center justify-center w-7 h-7 rounded text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                    :aria-label="`Open detail for ${tour.name}`"
                    title="Info"
                  >
                    <Info class="w-4 h-4" />
                  </button>
                  <button
                    @click.stop="toggleActionsMenu(tour.clientId, $event)"
                    class="flex items-center justify-center w-7 h-7 rounded text-gray-600 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                    :aria-label="`Actions for ${tour.name}`"
                  >
                    <EllipsisVertical class="w-4 h-4" />
                  </button>
                  <!-- Actions dropdown (teleported to body to escape scroll container) -->
                  <Teleport to="body">
                    <div
                      v-if="openActionsMenuId === tour.clientId"
                      :style="actionsMenuStyle"
                      class="fixed z-9999 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden overflow-y-auto"
                      role="menu"
                      :aria-label="`Actions for ${tour.name}`"
                      @click.stop
                    >
                      <button
                        @click="startInlineRename(tour)"
                        class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-50 text-left cursor-pointer"
                      >
                        <Pencil class="w-3.5 h-3.5 text-gray-400 shrink-0" /> rename
                      </button>
                      <button
                        @click="void downloadTourGpx(tour)"
                        class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-50 text-left cursor-pointer"
                      >
                        <Download class="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span>download GPX</span>
                        <Loader2
                          v-if="downloadingTourIds.has(tour.clientId)"
                          class="w-3 h-3 ml-auto animate-spin text-gray-400 shrink-0"
                        />
                      </button>
                      <button
                        @click="openManageCollections(tour, $event)"
                        class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-50 text-left cursor-pointer"
                      >
                        <FolderCog class="w-3.5 h-3.5 text-gray-400 shrink-0" /> manage collections
                      </button>
                      <button
                        @click="handleTogglePinned(tour)"
                        class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:bg-gray-50 text-left cursor-pointer"
                      >
                        <Pin
                          class="w-3.5 h-3.5 shrink-0"
                          :class="tour.isPinned ? 'fill-current text-amber-600' : 'text-gray-400'"
                        />
                        {{ tour.isPinned ? 'remove from Pinned' : 'pin' }}
                      </button>
                      <button
                        @click="openDeleteFromMenu(tour)"
                        class="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-red-600 transition-colors hover:bg-red-50 text-left cursor-pointer"
                      >
                        <Trash2 class="w-3.5 h-3.5 text-red-400 shrink-0" /> delete
                      </button>
                    </div>
                  </Teleport>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Rename dialog -->
    <BaseModal
      :is-open="renameDialogOpen"
      :title="
        renameTargetTours.length > 1 ? `Rename ${renameTargetTours.length} Tours` : 'Rename Tour'
      "
      size="sm"
      :show-footer="true"
      @close="closeRenameDialog"
    >
      <label class="block text-sm text-gray-600 mb-1">New name</label>
      <input
        type="text"
        v-model="renameValue"
        placeholder="Enter tour name…"
        class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
        @keydown.enter="submitRename"
      />
      <p v-if="renameTargetTours.length > 1" class="mt-1.5 text-xs text-gray-400">
        Use <code class="px-1 py-0.5 bg-gray-100 rounded text-gray-600">{#}</code> for a sequential
        number, e.g. <code class="px-1 py-0.5 bg-gray-100 rounded text-gray-600">Tour {#}</code> →
        Tour 1, Tour 2, …
      </p>
      <p v-if="renamePreview" class="mt-1 text-xs text-primary-600">
        {{ renamePreview.first }} … {{ renamePreview.last }}
      </p>

      <template #footer>
        <button
          @click="closeRenameDialog"
          class="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button
          @click="submitRename"
          :disabled="!renameValue.trim() || isRenaming"
          class="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {{ isRenaming ? 'Renaming…' : 'Rename' }}
        </button>
      </template>
    </BaseModal>

    <Teleport to="body">
      <TourDeleteModal
        :open="isSingleDeleteDialogOpen"
        :tour="singleDeleteTargetTour"
        :tour-name="singleDeleteTargetTour?.name ?? null"
        :deleting="isDeleting"
        :include-current-collection="false"
        @close="closeSingleDeleteDialog"
        @confirm="submitSingleDelete"
      />
    </Teleport>

    <!-- Delete confirmation dialog -->
    <BaseModal
      :is-open="deleteDialogOpen"
      :title="
        deleteTargetTours.length > 1 ? `Delete ${deleteTargetTours.length} Tours` : 'Delete Tour'
      "
      size="sm"
      :show-footer="true"
      role="alertdialog"
      @close="closeDeleteDialog"
    >
      <p class="text-sm text-gray-700">
        {{
          deleteTargetTours.length === 1
            ? `Are you sure you want to delete "${deleteTargetTours[0]?.name}"?`
            : `Are you sure you want to delete ${deleteTargetTours.length} tours?`
        }}
      </p>
      <p
        class="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
      >
        {{ deleteTargetTours.length === 1 ? 'This tour' : 'These tours' }} will be permanently
        deleted and removed from all collections. This action cannot be undone.
      </p>

      <template #footer>
        <button
          @click="closeDeleteDialog"
          class="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button
          @click="submitDelete"
          :disabled="isDeleting"
          class="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {{ isDeleting ? 'Deleting…' : 'Delete' }}
        </button>
      </template>
    </BaseModal>

    <!-- Manage Collections dialog -->
    <BaseModal
      :is-open="wsDialogOpen"
      title="Manage Collections"
      size="md"
      :show-footer="true"
      body-class="p-0"
      @close="closeWsDialog"
    >
      <template #title>
        Manage Collections
        <span class="font-normal text-gray-500 text-base">
          ({{ wsDialogTours.length }} tour{{ wsDialogTours.length !== 1 ? 's' : '' }})
        </span>
      </template>

      <div class="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
        <Search class="w-3.5 h-3.5 text-gray-400 shrink-0" />
        <input
          type="search"
          v-model="wsDialogSearch"
          placeholder="Search collections…"
          class="flex-1 min-w-0 text-sm border-none outline-none bg-transparent placeholder-gray-400"
        />
      </div>

      <div class="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
        <CreateCollectionIcon class="w-3.5 h-3.5 text-gray-400 shrink-0" />
        <input
          type="text"
          v-model="wsDialogNewName"
          placeholder="New collection name…"
          class="flex-1 min-w-0 text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
          @keydown.enter="wsDialogCreateWorkspace"
        />
        <button
          @click="wsDialogCreateWorkspace"
          :disabled="!wsDialogNewName.trim() || wsDialogIsCreating"
          class="px-2.5 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {{ wsDialogIsCreating ? 'Creating…' : 'Create' }}
        </button>
      </div>

      <div class="overflow-y-auto">
        <div
          v-for="ws in wsDialogFilteredWorkspaces"
          :key="ws.clientId"
          class="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-50 hover:bg-gray-50 transition-colors"
        >
          <div class="flex items-center gap-2 min-w-0">
            <SquarePlus class="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span class="text-sm text-gray-700 truncate">{{ ws.name }}</span>
            <span
              v-if="wsAssignedCount(ws.clientId) > 0"
              class="text-xs text-primary-600 whitespace-nowrap"
            >
              {{ wsAssignedCount(ws.clientId) }}/{{ wsDialogTours.length }}
            </span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <button
              v-if="wsAssignedCount(ws.clientId) < wsDialogTours.length"
              @click="bulkAssignWorkspace(ws.clientId)"
              :aria-label="`Assign selected tours to ${ws.name}`"
              :disabled="isWsDialogProcessing"
              class="px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded transition-colors cursor-pointer disabled:opacity-50"
            >
              Assign
            </button>
            <button
              v-if="wsAssignedCount(ws.clientId) > 0"
              @click="bulkRemoveWorkspace(ws.clientId)"
              :aria-label="`Remove selected tours from ${ws.name}`"
              :disabled="isWsDialogProcessing"
              class="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        </div>

        <BaseEmptyState
          v-if="wsDialogFilteredWorkspaces.length === 0"
          size="compact"
          title="No collections found"
        />
      </div>

      <template #footer>
        <button
          @click="closeWsDialog"
          class="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
        >
          Done
        </button>
      </template>
    </BaseModal>

    <!-- Manage collections — desktop anchored dropdown -->
    <Teleport to="body">
      <div
        v-if="!isSmallScreen && manageColOpen"
        class="fixed inset-0 z-9999"
        @click="closeManageCollections"
        @contextmenu.prevent="closeManageCollections"
      />
      <div
        v-if="!isSmallScreen && manageColOpen"
        :style="manageColDropdownStyle"
        class="fixed z-9999 bg-white border border-gray-200 rounded-md shadow-lg flex flex-col overflow-hidden"
        @click.stop
      >
        <!-- Search -->
        <div class="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100">
          <Search class="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            type="search"
            v-model="manageColSearch"
            placeholder="Search collections…"
            class="flex-1 min-w-0 text-sm border-none outline-none bg-transparent placeholder-gray-400"
          />
        </div>
        <!-- Collection list -->
        <div class="overflow-y-auto">
          <button
            v-for="ws in manageColFilteredWorkspaces"
            :key="ws.clientId"
            @click="toggleCollectionMembership(ws)"
            :disabled="manageColProcessing.has(ws.clientId)"
            class="flex w-full items-center gap-2 px-3 py-2 border-b border-gray-50 text-left cursor-pointer transition-colors"
            :class="
              isTourInWorkspace(ws.clientId)
                ? 'bg-emerald-50 hover:bg-emerald-100'
                : 'hover:bg-gray-50'
            "
          >
            <Loader2
              v-if="manageColProcessing.has(ws.clientId)"
              class="w-3.5 h-3.5 animate-spin text-gray-400 shrink-0"
            />
            <Check
              v-else-if="isTourInWorkspace(ws.clientId)"
              class="w-3.5 h-3.5 text-emerald-600 shrink-0"
            />
            <FolderPlus v-else class="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span
              class="text-sm flex-1 truncate"
              :class="
                isTourInWorkspace(ws.clientId) ? 'text-emerald-700 font-medium' : 'text-gray-800'
              "
              >{{ ws.name }}</span
            >
            <Globe v-if="ws.isPublic" class="w-3 h-3 text-gray-400 shrink-0" title="Public" />
            <Users
              v-else-if="!ws.isOwner"
              class="w-3 h-3 text-gray-400 shrink-0"
              title="Shared with you"
            />
          </button>
          <div
            v-if="manageColFilteredWorkspaces.length === 0"
            class="px-3 py-4 text-sm text-gray-400 text-center"
          >
            No collections found
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Row context menu — mobile bottom sheet -->
    <MobileActionSheet
      v-if="isSmallScreen && contextMenuTour"
      :open="contextMenuOpen"
      :title="contextMenuTour.name"
      :dot-color="contextMenuTourAccentColor"
      @close="closeContextMenu"
      @closed="handleContextMenuClosed"
    >
      <template #header-left>
        <button
          type="button"
          class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
          title="Close"
          aria-label="Close tour actions"
          @click="closeContextMenu"
        >
          <X class="h-4 w-4" />
        </button>
      </template>

      <LibraryTourActionSheetContent
        :tour="contextMenuTour"
        :is-downloading="downloadingTourIds.has(contextMenuTour.clientId)"
        @rename="startInlineRename(contextMenuTour)"
        @download="void downloadTourGpx(contextMenuTour)"
        @manage-collections="openAddToCollectionForTours([contextMenuTour])"
        @toggle-pinned="handleTogglePinned(contextMenuTour)"
        @view-details="handleContextMenuViewDetails"
        @delete="openDeleteFromMenu(contextMenuTour)"
      />
    </MobileActionSheet>

    <!-- Manage collections — mobile bottom sheet -->
    <Teleport to="body">
      <div
        v-if="isSmallScreen && manageColOpen"
        class="fixed inset-0 z-200 flex flex-col justify-end"
        @click.self="closeManageCollections"
      >
        <div class="absolute inset-0 bg-black/30" @click="closeManageCollections" />
        <div
          class="relative bg-white rounded-t-2xl shadow-xl flex flex-col h-[60vh]"
          :style="{
            paddingBottom: 'max(env(safe-area-inset-bottom), 8px)',
            transition: manageColClosing ? 'transform 0.3s ease' : 'none',
            ...manageColSheetStyle,
          }"
          @click.stop
        >
          <!-- Drag handle -->
          <div
            class="flex justify-center pt-2.5 pb-1 shrink-0 cursor-grab touch-none"
            @touchstart.passive="onManageDragStart"
            @touchmove.passive="onManageDragMove"
            @touchend="onManageDragEnd"
            @touchcancel="onManageDragEnd"
          >
            <div class="w-9 h-1 rounded-full bg-gray-300" />
          </div>
          <!-- Header -->
          <div class="px-4 py-2 shrink-0 border-b border-gray-100">
            <p class="text-xs text-gray-400 uppercase tracking-wide font-semibold">
              Manage collections
            </p>
            <p class="text-sm font-medium text-gray-700 truncate">
              {{ manageColTour?.name ?? '' }}
            </p>
          </div>
          <!-- Search -->
          <div class="flex items-center gap-2 px-4 py-2 shrink-0 border-b border-gray-100">
            <Search class="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="search"
              v-model="manageColSearch"
              placeholder="Search collections…"
              class="flex-1 min-w-0 text-sm border-none outline-none bg-transparent placeholder-gray-400"
            />
          </div>
          <!-- Collection list -->
          <div class="flex-1 overflow-y-auto">
            <button
              v-for="ws in manageColFilteredWorkspaces"
              :key="ws.clientId"
              @click="toggleCollectionMembership(ws)"
              :disabled="manageColProcessing.has(ws.clientId)"
              class="flex w-full items-center gap-3 px-4 py-3 border-b border-gray-50 text-left cursor-pointer transition-colors"
              :class="
                isTourInWorkspace(ws.clientId)
                  ? 'bg-emerald-50 hover:bg-emerald-100'
                  : 'hover:bg-gray-50'
              "
            >
              <Loader2
                v-if="manageColProcessing.has(ws.clientId)"
                class="w-4 h-4 animate-spin text-gray-400 shrink-0"
              />
              <Check
                v-else-if="isTourInWorkspace(ws.clientId)"
                class="w-4 h-4 text-emerald-600 shrink-0"
              />
              <FolderPlus v-else class="w-4 h-4 text-gray-400 shrink-0" />
              <span
                class="text-sm flex-1 truncate"
                :class="
                  isTourInWorkspace(ws.clientId) ? 'text-emerald-700 font-medium' : 'text-gray-800'
                "
                >{{ ws.name }}</span
              >
              <Globe v-if="ws.isPublic" class="w-3 h-3 text-gray-400 shrink-0" title="Public" />
              <Users
                v-else-if="!ws.isOwner"
                class="w-3 h-3 text-gray-400 shrink-0"
                title="Shared with you"
              />
            </button>
            <div
              v-if="manageColFilteredWorkspaces.length === 0"
              class="px-4 py-6 text-sm text-gray-400 text-center"
            >
              No collections found
            </div>
          </div>
          <!-- Footer -->
          <div class="shrink-0 px-4 py-3 border-t border-gray-100">
            <button
              @click="closeManageCollections"
              class="w-full py-2.5 rounded-lg bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* Dual-range slider */
.arc-slider {
  position: absolute;
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  pointer-events: none;
  background: transparent;
  height: 0;
}
.arc-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  pointer-events: all;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #10b981;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}
.arc-slider::-moz-range-thumb {
  pointer-events: all;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #10b981;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  border: none;
}
</style>
